import { Fragment, useState, type ReactNode } from "react";
import {
  ArrowLeftRight,
  BellRing,
  Bike,
  ChefHat,
  ChevronDown,
  Clock,
  CircleCheck,
  Gift,
  HandPlatter,
  Pencil,
  Percent,
  Printer,
  ReceiptText,
  RotateCcw,
  StickyNote,
  Trash2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { usePaperTill } from "@/shared/posTheme";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { paperStatusSections, type FireState, type StatusSectionState } from "../lib/paperFires";
import { formatSectionStamp, sectionStartedAt } from "../lib/sectionTime";
import { useLocalName } from "@/shared/localName";
import { Button, Checkbox, Modal } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { markablePlates } from "../lib/partyShares";
import { invoiceForPlates } from "@/modules/pos/lib/preBillCheck";
import { paidPlateIds } from "../lib/settleSheet";
import {
  billCustomerLines,
  undeliverableMessage,
  undeliverableReason,
} from "@/lib/invoice/place";
import { moneyAmount, TillPhoneControls, useMoney } from "@/shared/ui";
import { tenderMethodsLabel } from "@/lib/settlementMethodLabel";
import { tenderReferencesOf } from "@/modules/pos/lib/tenderReference";
import type { Invoice, InvoiceItem } from "@/lib/api/types";
import type { PlateFoodFate } from "@/shared/invoices/foodFate";
import {
  groupPlates,
  liveOpenTotals,
  tillTotals,
  type PlateGroup,
} from "@/modules/pos/lib/groupPlates";
import {
  billDiscountPricingOf,
  invoiceChargeRates,
  listSubtotal,
} from "@/modules/pos/lib/pricing";
import {
  billDiscountReasonLabel,
  storedBillDiscountLine,
} from "@/modules/pos/lib/billDiscount";
import { isRefundOfferable } from "@/modules/pos/lib/refundAllocation";
import {
  outstandingOnBill,
  overpaidOnBill,
} from "@/modules/pos/lib/partyShares";
import type { RefundSlipButton } from "@/modules/pos/lib/refundDocument";
import { captainFirstName } from "@/modules/pos/lib/captain";
import {
  tillCloseAction,
  tillPrimaryAction,
  type CloseBlocking,
  type CloseUnblockKind,
} from "@/modules/pos/lib/tillActions";
import { CloseBlockedNote, closeBlockedReasonId, useCloseBlockedText } from "./CloseBlockedNote";
import { OffPremiseOrderHead, type OffPremiseHeadContact } from "./delivery/OffPremiseOrderHead";
import { OrderedByLine } from "./delivery/OrderedByLine";

/**
 * The order panel — the redesign's right-hand column (`Trioty POS.dc.html`,
 * order artboard). 420px, its own scroll, a pinned totals/actions foot.
 *
 * ## The one number that is NOT the artboard's
 *
 * The column is 420px, where the mock draws 372px. At 372 the panel clipped
 * its own content once the real screen carried everything at once: the
 * table-transfer chips ran off the inline-start edge mid-word, and a line row
 * holding a stepper, a price, a remove button and two follow-up chips
 * overlapped itself. The artboard was drawn on a quieter bill than the one the
 * app now renders. Widening the column was the cheaper fix — the alternative
 * was shrinking controls a cashier taps with a thumb.
 *
 * From 1,280px both tills' panel is `clamp(420px, 28vw, 520px)` and a line's
 * name and price sit one type step up (founder, 2026-10-15, F35 → D159: the
 * names and prices read small on a desktop). Below 1,280px — and so at the
 * 390px design width — nothing moved.
 *
 * Every OTHER measurement here is still the mock's, not an approximation:
 * 14/16/10
 * header padding, 9px line rows on a 1px hairline, a 24px stepper in a 2px
 * inset track, a 64px end-aligned line total, 11.5px muted total rows over a
 * dashed rule, and a 17px/800 figure. Where a number here looks arbitrary it
 * came from the artboard.
 *
 * WHAT THE MOCK CANNOT HAVE, AND WHY
 * ----------------------------------
 * The mock's cart is client state, so its rows freely increment, decrement and
 * delete. A real invoice line is server state behind a command log, and what a
 * row allows depends on where its food is (D18's edit matrix).
 *
 * So the stepper is rendered exactly as drawn, and wired honestly:
 *   basket  -> +/-/x edit the basket outright; nobody has seen these yet (D17)
 *   queued  -> + adds another; - pulls it back, which is a VOID with a reason,
 *              because the kitchen has already been told about it
 *   cooking -> + adds another; -/x open the void dialog, and voiding is the
 *              cashier's authority, not the waiter's
 *
 * Rows are GROUPED for display only (D15). One database row is one plate, which
 * is what lets a single returned burger be voided out of three; the panel
 * collapses siblings that are identical in every respect a waiter could act on,
 * and splits them the moment one diverges.
 *
 * The mock's Hold control is not rendered at all. There is no hold command, and
 * a button that looks live and does nothing is worse than an absent one.
 */

/** One chip in the panel's table row. */
export type PanelTable = {
  tableId: string;
  name: string;
  /** The FIRST open invoice on this table, when there is one (B8). */
  invoiceId: string | null;
  /**
   * How many open bills this table carries (B8) — many are legal now, with no
   * cap and no owner. A switcher tap on a table with more than one must NOT
   * pick one: it lands on the table screen, where they are listed, because
   * opening a stranger's ticket is the failure this cardinality introduces.
   */
  openBillCount: number;
  /** A CLOSED-unsettled bill is still out on this table (D24's cheque out).
   *  Display only — the table is still a legal transfer target — but the chip
   *  must not read as plainly FREE while the floor grid one screen back shows
   *  the same table warning-toned. */
  chequeOut?: boolean;
};

/**
 * Where the food is, as a short line under the dish.
 *
 * Only shown once the plate has left the basket and only while it is still in
 * motion: "served" is the expected end state and saying so on every finished
 * line would be noise on a busy ticket.
 */
function kitchenNote(
  t: ReturnType<typeof useTranslate>,
  state: InvoiceItem["fulfillmentState"],
): string | null {
  switch (state) {
    case "QUEUED":
      return t((m) => m.invoices.itemQueued);
    case "PREPARING":
      return t((m) => m.invoices.itemPreparing);
    case "READY":
      return t((m) => m.invoices.itemReady);
    default:
      return null;
  }
}

/**
 * The plate's own line: where the food is, and whether it has been sent back.
 *
 * ONE slot, TWO facts, and B117/A1 is what happens when a slot that can only
 * hold one is given a precedence chain: `remakeCount` outranked
 * `kitchenNote(state)`, so a remade plate lost its kitchen state permanently
 * and the close refused over a line nothing on screen marked as waiting.
 *
 * State first, because it is the live fact — "«بيتحضّر» · إعادة تحضير ×1" reads
 * as a plate being cooked again, which is what it is. A SERVED plate has no
 * state line at all (see `kitchenNote`), so the remake badge stands alone there
 * and the finished ticket reads exactly as it always did.
 */
function plateNote(
  t: ReturnType<typeof useTranslate>,
  state: InvoiceItem["fulfillmentState"],
  remakeCount: number,
): string | null {
  const state_ = kitchenNote(t, state);
  const remade =
    remakeCount > 0 ? `${t((m) => m.pos.remake)} ×${remakeCount}` : null;
  if (state_ && remade) return `${state_} · ${remade}`;
  return state_ ?? remade;
}

/** PO-9: the food-fate words — the stock movement's own («مرتجع من المطبخ» / «هالك»). */
function foodFateLabel(
  t: ReturnType<typeof useTranslate>,
  fate: PlateFoodFate,
): string {
  return fate === "RETURNED"
    ? t((m) => m.pos.returnToStockReturned)
    : t((m) => m.pos.returnToStockWasted);
}

function statusSectionLabel(
  t: ReturnType<typeof useTranslate>,
  state: StatusSectionState,
): string {
  return state === "VOIDED" ? t((m) => m.invoices.itemVoided) : fireStateLabel(t, state);
}

function fireStateLabel(t: ReturnType<typeof useTranslate>, state: FireState): string {
  switch (state) {
    case "QUEUED":
      return t((m) => m.invoices.itemQueued);
    case "PREPARING":
      return t((m) => m.invoices.itemPreparing);
    case "READY":
      return t((m) => m.invoices.itemReady);
    default:
      return t((m) => m.invoices.itemServed);
  }
}


const HAIRLINE = "border-b border-[color:var(--border-light)]";

/** A line's name block — a button when the line can be reopened (PAPER drafts). */
function LineBody({
  onOpen,
  testId,
  title,
  children,
}: {
  onOpen?: () => void;
  testId: string;
  title?: string;
  children: ReactNode;
}) {
  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      title={title}
      data-ui="line-open"
      data-testid={`${testId}-open`}
      className="min-w-0 flex-1 text-start"
    >
      {children}
    </button>
  ) : (
    <div className="min-w-0 flex-1">{children}</div>
  );
}

/**
 * One row, shared by basket and fired groups so both keep the mock's geometry.
 *
 * Its sizes read the platform type scale (`--font-size-*`), which a device's
 * till text size redeclares (D162, `app/styles.css`); from 1,280px the name and
 * the line total read the next token up (D159) — the `xl:` classes here for the
 * dark till, `_paper-till.css` for the light one.
 */
function LineRow({
  name,
  unit,
  priceOverride = false,
  addonLabel,
  comboLabel = null,
  substitutionLabel = null,
  instruction,
  note,
  noteTone = "info",
  quantity,
  total,
  onPlus,
  onMinus,
  onRemove,
  disabled,
  testId,
  trailing,
  leading,
  thumb,
  onOpen,
  openTitle,
  compact = false,
  aside,
  kind,
}: {
  name: string;
  unit: string;
  /**
   * D46 (S9): this plate's price was typed by hand, not read off the
   * catalogue. Shown as a small chip beside the unit price — the one number
   * on the row it actually describes — never as a colour change on the row
   * itself, which a busy ticket would read as a status rather than a fact
   * about where the price came from.
   */
  priceOverride?: boolean;
  addonLabel: string | null;
  /**
   * What is on the plate if it is a combo (S16, D52). A sub-line under the
   * dish, in the same slot the addon label uses — a waiter reading "وجبة"
   * beside a price needs to see what the customer is getting for it. No
   * prices: a component has none.
   */
  comboLabel?: string | null;
  /**
   * The plate's ingredient swaps (D61) — "7up ← V7".
   *
   * ABOVE the addon label and in the warning colour, because a swap changes
   * what the plate IS and what it costs: a line priced above the menu with
   * nothing saying why is a charge the cashier cannot defend when the guest
   * asks, which is the gap D46's chip closed for the hand-typed price.
   */
  substitutionLabel?: string | null;
  /** The kitchen note — starred like the chit stars it, so both read alike. */
  instruction?: string | null;
  note: string | null;
  /** `success` is PAPER's "Paid" tag on a plate a settled party owns. */
  noteTone?: "info" | "danger" | "warning" | "success";
  quantity: number;
  total: string;
  onPlus?: () => void;
  onMinus?: () => void;
  onRemove?: () => void;
  disabled: boolean;
  testId: string;
  trailing?: ReactNode;
  /** A control before everything else on the row — the line's checkbox. */
  leading?: ReactNode;
  /** PAPER: the dish's 28px photo or badge at the row's start (a new line). */
  thumb?: ReactNode;
  /**
   * PAPER (2026-10-03): a NEW line's body reopens the picker on it. Only the
   * body — the steppers and ✕ beside it keep their own acts.
   */
  onOpen?: () => void;
  openTitle?: string;
  /**
   * A SENT line in PAPER (founder, 2026-10-06): the stepper stays, the ✕ does
   * not — cancelling, like the line's other acts (swap, remake, comp …), is in
   * the line's actions sheet, opened by tapping the line.
   */
  compact?: boolean;
  /** A small control after the total — a basket line's own Send (2026-10-28). */
  aside?: ReactNode;
  /**
   * "sent" marks a fired row (waiter phone, 2026-10-28): below `lg` it reads
   * as the design draws it — photo, name, amount — and its stepper and next-
   * step chip give way to the line's sheet, opened by tapping the row.
   */
  kind?: "sent";
}) {
  const t = useTranslate();
  // PAPER hooks for the stepper and the total (the redesign's bare 24px
  // squares and 58px figure); every other look keeps the markup as it was.
  const paper = usePaperTill();
  return (
    <div
      data-ui="table-row"
      data-line-kind={kind}
      className={`pos-line py-[9px] ${HAIRLINE}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        {leading ? <span data-ui="line-pick" className="flex shrink-0 items-center">{leading}</span> : null}
        {thumb ? (
          <span data-ui="line-thumb" aria-hidden="true" className="flex shrink-0 items-center justify-center overflow-hidden">
            {thumb}
          </span>
        ) : null}
        <LineBody onOpen={onOpen} testId={testId} title={openTitle}>
          <div data-ui="line-name" className="truncate text-[length:calc(var(--font-size-xs)_+_0.5px)] font-bold text-[color:var(--text)] xl:text-[length:calc(var(--font-size-sm)_+_0.5px)]">
            {name}
          </div>
          <div data-ui="line-meta" className="mt-px flex items-center gap-1.5 text-[length:var(--font-size-2xs)] font-semibold tabular-nums text-[color:var(--text-muted)]">
            {unit}
            {priceOverride ? (
              <span className="rounded-full border border-[color:var(--warning)] px-1.5 py-px text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.04em] text-[color:var(--warning)]">
                {t((m) => m.pos.priceOverrideChip)}
              </span>
            ) : null}
          </div>
          {comboLabel ? (
            <div
              className="mt-px truncate text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]"
              data-testid="line-combo-components"
            >
              {comboLabel}
            </div>
          ) : null}
          {substitutionLabel ? (
            <div
              className="mt-px truncate text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
              data-testid="line-substitutions"
            >
              {substitutionLabel}
            </div>
          ) : null}
          {addonLabel ? (
            <div className="mt-px truncate text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-disabled)]">
              + {addonLabel}
            </div>
          ) : null}
          {instruction ? (
            <div className="mt-px truncate text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]">
              {`★ ${instruction}`}
            </div>
          ) : null}
          {note ? (
            <div
              className={[
                "mt-px truncate text-[length:var(--font-size-2xs)] font-semibold",
                noteTone === "danger"
                  ? "text-[color:var(--danger)]"
                  : noteTone === "warning"
                    ? "text-[color:var(--warning)]"
                    : noteTone === "success"
                      ? "text-[color:var(--success)]"
                      : "text-[color:var(--info)]",
              ].join(" ")}
              data-ui={noteTone === "success" ? "line-paid" : undefined}
            >
              {note}
            </div>
          ) : null}
        </LineBody>

        {/* 24px controls in a 2px inset track — the mock's stepper. */}
        <div
          data-ui={paper ? "line-stepper" : undefined}
          className="flex shrink-0 items-center rounded-[var(--radius-tile)] border border-[color:var(--border-strong)] bg-[color:var(--pos-track)] p-0.5"
        >
          <button
            type="button"
            data-ui={paper ? "line-step" : undefined}
            onClick={onMinus}
            disabled={disabled || !onMinus}
            aria-label={t((m) => m.pos.lineLess)}
            data-testid={`${testId}-minus`}
            className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-control)] text-[color:var(--text-secondary)] transition-colors enabled:hover:bg-[color:var(--surface-alt)] enabled:hover:text-[color:var(--text)] disabled:opacity-40"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
            </svg>
          </button>
          <span
            data-ui={paper ? "line-qty" : undefined}
            className="min-w-[22px] text-center text-xs font-extrabold tabular-nums"
          >
            {quantity}
          </span>
          <button
            type="button"
            data-ui={paper ? "line-step" : undefined}
            onClick={onPlus}
            disabled={disabled || !onPlus}
            aria-label={t((m) => m.pos.lineMore)}
            data-testid={`${testId}-plus`}
            className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-control)] text-[color:var(--text-secondary)] transition-colors enabled:hover:bg-[color:var(--surface-alt)] enabled:hover:text-[color:var(--text)] disabled:opacity-40"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>

        {/* The artboard pins this column at 64px, which fits its bare `320`.
            Ours carries the currency word too (`جنيه 320`), and a wrapped line
            total in a bill is worse than a column that differs from the
            artboard — but the fix for that was a FIXED 96px, which reserved
            two and a half times what `25 جنيه` needs and charged the
            difference to the dish name beside it. On a 420px panel the row
            then fit with zero slack: the name was the only flexible child, so
            it absorbed every pixel the others took and truncated first.

            Content-sized with a 64px floor instead. It cannot wrap — an
            auto-width box is exactly as wide as its text — while a long total
            still grows and a short one hands ~32px back to the name. */}
        <div
          data-ui={paper ? "line-total" : undefined}
          className="min-w-16 shrink-0 text-end text-xs font-extrabold tabular-nums xl:text-[length:var(--font-size-sm)]"
        >
          {total}
        </div>
        {aside}

        {/* `--text-muted`, not `--text-disabled`, and the swap is a contrast
            fix rather than a taste one. Measured on this row's own three
            backgrounds, `--text-disabled` #67675f reads 3.17:1 on `--surface`,
            3.01:1 on `--surface-alt` and 2.64:1 on the stepper track — under
            1.4.11's 3:1 on the track it actually sits against, and under
            1.4.3 everywhere. It is also the ONLY destructive control on the
            row, and it was the faintest AND the smallest thing on it, which is
            backwards. `--text-muted` measures 5.21:1. The hover still goes to
            `--danger`; nothing else about the control moves. */}
        {compact ? null : (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled || !onRemove}
          aria-label={t((m) => m.pos.lineRemove)}
          data-ui="close"
          data-testid={`${testId}-remove`}
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[color:var(--text-muted)] transition-colors enabled:hover:bg-[color:var(--surface-alt)] enabled:hover:text-[color:var(--danger)] disabled:opacity-40"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        )}
      </div>
      {trailing}
    </div>
  );
}

export function OrderPanel({
  invoice,
  sentItems,
  drafts,
  pending,
  dinerPayment,
  bindLabel,
  busyKey,
  isBusy,
  canAppend,
  canFire,
  canServe,
  canPrepare,
  canStartLine = canPrepare,
  canPrint,
  canVoid,
  canComp = false,
  canReopen = false,
  canRemake,
  canSubstitute,
  kitchenPrintsChits,
  canClose,
  canSettle,
  canCancel,
  onCancel,
  onReopen,
  canRefund,
  onRefund,
  refundReceipts,
  onPrintRefundReceipt,
  canTransfer,
  onTransfer,
  canEditCustomer,
  onEditCustomer,
  canEditCharges,
  onEditCharges,
  platformName,
  onRefundOverpayment,
  onEditNote,
  onFireGroup,
  onDraftPlus,
  onDraftMinus,
  onDraftRemove,
  onDraftEdit,
  onSentPlus,
  onSentVoid,
  onRemake,
  onRemakeBlocked,
  onAmendSwaps,
  onServe,
  onStartPreparing,
  onMarkReady,
  onPrintChit,
  onFire,
  onRequestCashier,
  onClose,
  closeAs = "close",
  closeNote,
  onSettle,
  onSettleOpen,
  onPrint,
  conflict,
  closeBlocking,
  closeUnblock = null,
  onCloseUnblock,
  tables,
  currentTableId,
  onPickTable,
  onNewOrder,
  onBackToTables,
  tableName,
  dishThumb,
  canStart,
  narrow,
  canCheck = false,
  onCheck,
  canDiscount = false,
  onDiscount,
  onDiscountRemove,
  onPrintItems,
  onSettleItems,
  storeTimezone,
  headGuests,
  headCaptain,
  offPremiseHead,
  billDispatch,
  handOver = false,
  dispatchFoot,
}: {
  /**
   * `null` on the pre-order view: the table is free and no invoice exists yet.
   * Opening one is an explicit act ("Start order"), not a side effect of
   * tapping the table.
   */
  invoice: Invoice | null;
  sentItems: InvoiceItem[];
  /** The waiter's basket — server-side DRAFT plates (D17). */
  drafts: InvoiceItem[];
  pending: ReactNode;
  /**
   * A diner's own claim to have paid this bill (D44).
   *
   * A SLOT rather than props, for the same reason `pending` and `conflict` are:
   * this panel arranges the bill, and what a payment claim looks like and who
   * may act on it belongs to the page that knows the session.
   *
   * Above the lines, not beside the settle button: a cashier reaching for
   * settle needs to have already seen it, and a card below the fold is a card
   * that gets settled past.
   */
  dinerPayment: ReactNode;
  bindLabel: string;
  busyKey: string | null;
  isBusy: boolean;
  canAppend: boolean;
  canFire: boolean;
  canServe: boolean;
  /**
   * Holds `RESTAURANT_ITEM_PREPARE` — the rung the kitchen board checks. The
   * till offers the same two transitions so a store whose board is dead (or a
   * cashier who never opens one) can still move a plate off QUEUED, which D22
   * requires before the invoice may close.
   */
  canPrepare: boolean;
  /**
   * Offer a line's «بدء» (QUEUED → PREPARING, sent as the TILL's start, D165).
   * Defaults to `canPrepare`. False in the paper workflows (PAPER_ONLY,
   * PAPER_AND_PASS): there the printed ticket is the actuator (D18), the board
   * refuses the same act for the same reason (`startsFromBoard`), and the
   * recovery is «إعادة إرسال التذكرة» beside the greyed Close.
   */
  canStartLine?: boolean;
  /** May print a chit — shown only where paper means something (print mode). */
  canPrint: boolean;
  canVoid: boolean;
  /** `RESTAURANT_INVOICE_COMP` — the comp chip, apart from void since 2026-11-02. */
  canComp?: boolean;
  /** `RESTAURANT_INVOICE_REOPEN` — undoing a close, apart from closing since 2026-11-02. */
  canReopen?: boolean;
  canRemake: boolean;
  /**
   * Holds `RESTAURANT_INVOICE_SUBSTITUTE` — the rung an add-time swap already
   * needs (D63). Amending a plate's swaps (B19) is the same authority, so it
   * is the same rung and not a new one.
   */
  canSubstitute: boolean;
  /**
   * The STORE prints chits (`kitchenPrintEnabled`) — not "this user may print",
   * which is `canPrint` above and is a permission.
   *
   * It decides how far the amend chip reaches (B40/D85). On paper the chit is
   * the actuator (D18), so a plate is `PREPARING` because the printer moved it
   * there and not because a cook pressed start — the server allows the
   * amendment and the dialog warns. On a screen kitchen the same state means a
   * person really did start, and the chip stops at QUEUED.
   */
  kitchenPrintsChits: boolean;
  canClose: boolean;
  canSettle: boolean;
  /**
   * The invoice may still be walked away from: nothing the kitchen was told
   * about exists on it (the server's cancel gate, mirrored by the caller).
   * Drafts don't count — cancelling discards them, which is what it means.
   */
  canCancel: boolean;
  /** Asks for confirmation upstream; this button never cancels directly. */
  onCancel: () => void;
  /** The undo for a mistaken close — CLOSED footer only, cashier authority. */
  onReopen: () => void;
  /**
   * Holds `RESTAURANT_INVOICE_REFUND` (R9) — its OWN rung, in no preset role
   * below OWNER, granted per person through a custom role. Refunding a settled
   * bill and pocketing the cash is the most direct theft path in a restaurant
   * POS and the drawer still balances afterwards, so the owner must be able to
   * change their mind about a specific person. Hiding the control is the
   * courtesy; the server enforces the same rung before it claims the command
   * id, and it is the gate.
   */
  canRefund: boolean;
  /** Opens the refund sheet for the settled bill. Never refunds directly. */
  onRefund: () => void;
  /**
   * THE REFUNDS OF THIS BILL, newest first — one printable slip each (trap 9).
   *
   * A list and not a single control, because a refund is an EVENT: two partial
   * refunds off one cheque are two customer-facing tax documents with their own
   * money, method, reason, moment and plates. One button used to print a
   * COMBINED copy of both, which is a document about no single thing that
   * happened.
   *
   * Empty (or absent) means this bill has had nothing refunded — never "you
   * have reloaded": the events are on the invoice read, so a reload, another
   * device and tomorrow all show the same list.
   */
  refundReceipts?: readonly RefundSlipButton[];
  /**
   * Prints ONE refund's slip, named by its command id.
   *
   * It lives beside Print rather than on a toast because a customer-facing tax
   * document must not be behind a control that dismisses itself after three
   * seconds; the customer is still standing there — and it must still be there
   * tomorrow, when they come back with a jammed slip.
   */
  onPrintRefundReceipt?: (commandId: string) => void;
  /** The party changed seats. Shown only while the invoice is open. */
  canTransfer: boolean;
  onTransfer: () => void;
  /**
   * B107 — may correct who an off-premise bill is for.
   *
   * `RESTAURANT_INVOICE_APPEND`, the same rung as putting food on the bill and
   * as marking whose plates are whose: correcting a street name is floor work
   * and moves no money. The page resolves both the rung and the bill's state
   * (OPEN or CLOSED, never SETTLED), so this component carries no rule of its
   * own about when the control appears — the same shape `canTransfer` has.
   */
  canEditCustomer: boolean;
  onEditCustomer: () => void;
  /**
   * T6 — may correct what an off-premise bill CHARGES: its channel, its fee
   * and its platform. `RESTAURANT_INVOICE_CLOSE` — the cashier's rung, because
   * unlike the customer correction this one reprices the bill — and only
   * while OPEN. The page resolves rung and state, as for `canEditCustomer`.
   */
  canEditCharges: boolean;
  onEditCharges: () => void;
  /** The platform's NAME when the bill names one (cashier persona, 2026-10-07: the bill never said). */
  platformName: string | null;
  /**
   * B89 — hand back money the bill no longer asks for. Gated on `canRefund`
   * above, which is R9's rung: money physically leaving the till is the refund
   * rung's subject whatever prompted it.
   */
  onRefundOverpayment: () => void;
  /** Opens the kitchen-note editor for a basket group (DRAFT only). */
  onEditNote: (group: PlateGroup) => void;
  /** Fires ONE basket group — coursing: starters now, mains stay staged. */
  onFireGroup: (group: PlateGroup) => void;
  onDraftPlus: (group: PlateGroup) => void;
  onDraftMinus: (group: PlateGroup) => void;
  onDraftRemove: (group: PlateGroup) => void;
  /** PAPER: reopen the picker on a NEW line, to change it (founder, 2026-10-03). */
  onDraftEdit?: (group: PlateGroup) => void;
  onSentPlus: (group: PlateGroup) => void;
  onSentVoid: (group: PlateGroup, comp: boolean) => void;
  onRemake: (group: PlateGroup) => void;
  /**
   * WHY THE REMAKE CHIP IS GREY (B117/RT-C).
   *
   * Called instead of `onRemake` on a plate the kitchen has not started. The
   * chip used to be `disabled`, which on a touch till is a control that
   * swallows the tap and explains nothing — the head chef and the floor manager
   * independently made this their one insisted change, because the waiter taps,
   * nothing happens, and they walk to the pass mid-service to ask why.
   *
   * It raises the sentence LOCALLY. The server owns the same refusal
   * (`RESTAURANT_ITEM_NOT_STARTED`) and would say the same thing, but a round
   * trip to be told what this screen already knows is a refusal the till can
   * answer itself.
   */
  onRemakeBlocked: (group: PlateGroup) => void;
  /**
   * Opens the swap dialog for a group, to CHANGE what is already on the plate
   * (B19) rather than to compose a new one.
   *
   * Offered on a basket group and on a group the kitchen is still queueing,
   * and on nothing else: from `PREPARING` the server refuses, and a chip that
   * only ever produces a refusal is worse than no chip.
   */
  onAmendSwaps: (group: PlateGroup) => void;
  /** Marks every plate in the group delivered — PREPARING and READY both count. */
  onServe: (group: PlateGroup) => void;
  /** QUEUED -> PREPARING for the whole group. */
  onStartPreparing: (group: PlateGroup) => void;
  /** PREPARING -> READY for the whole group. */
  onMarkReady: (group: PlateGroup) => void;
  /** Reprints the chit this group was fired on. */
  onPrintChit: (ticketId: string) => void;
  onFire: () => void;
  /**
   * D120 — "Send to cashier": the primary of a session that builds orders but
   * may not send them to the kitchen.
   */
  onRequestCashier?: () => void;
  onClose: () => void;
  /**
   * What the close control reads. "settle" on the dark till's OPEN DIRECT
   * takeaway for a session that may close and settle (founder, 2026-10-08):
   * the control opens the pay sheet, which takes the money — so it is Settle,
   * and like PAPER's Settle it does NOT wait for the kitchen (D126): only busy
   * state disables it. Same control, same `onClose`.
   */
  closeAs?: "close" | "settle";
  /**
   * One line above the foot's controls saying what the close is waiting on —
   * the dark till's direct takeaway paid in full while the kitchen still has
   * plates. Omitted = nothing rendered.
   */
  closeNote?: string;
  onSettle: () => void;
  /**
   * PAPER only (2026-10-03): settle an OPEN bill from its footer — the
   * redesign's Settle, which takes Close's place. Passed only when the session
   * may both close and settle; absent, the footer keeps its Close.
   */
  onSettleOpen?: () => void;
  onPrint: () => void;
  conflict: ReactNode;
  /** What must be resolved before the bill can close (D22), by kind. */
  closeBlocking: CloseBlocking;
  /**
   * T6 / D173 — the one tap beside a greyed Close (`closeUnblockAction`):
   * «بدء التحضير والإغلاق» or «إعادة إرسال التذكرة». Null/absent = the reason
   * renders alone.
   */
  closeUnblock?: CloseUnblockKind | null;
  onCloseUnblock?: () => void;
  tables: PanelTable[];
  currentTableId: string;
  onPickTable: (table: PanelTable) => void;
  /** The artboard's "New order" — back to the floor to pick the next table. */
  /**
   * Ring up the NEXT party on this same table — the settled bill's most likely
   * sequel, since settling is what frees the table.
   */
  onNewOrder: () => void;
  /**
   * Back to the floor.
   *
   * Its own action because "new order" is not one: the button used to carry
   * that label and navigate to the grid, so a cashier who wanted the floor and
   * a cashier who wanted the next party both pressed it and one of them was
   * surprised. Two intentions, two buttons, each doing what it says.
   */
  onBackToTables: () => void;
  /** Names the table in the header while there is no invoice to name. */
  tableName?: string;
  /** PAPER: the dish's thumbnail for a new line, from the page's menu. */
  dishThumb?: (productId: string) => ReactNode;
  /** Whether this user may open an invoice. Pre-order view only: it decides
   *  which line the empty state ends on. */
  canStart?: boolean;
  /**
   * Whether this panel is one half of a SWITCHED pair at narrow widths — the
   * twin of `MenuBoard`'s prop of the same name, and the same contract.
   *
   * `undefined` keeps the legacy stacked behaviour (the pre-order table view).
   * `"show"` makes the panel FILL the narrow viewport, which is what pins its
   * foot to the bottom of the pane instead of leaving it floating mid-screen
   * on a short bill; `"hide"` takes it off the narrow layout entirely while
   * leaving the desktop column exactly as it was. Above `lg` all three render
   * the same 420px column.
   */
  narrow?: "show" | "hide";
  /**
   * Round 5 — may print a PRE-BILL CHECK: `RESTAURANT_INVOICE_APPEND`, the
   * rung the server puts `printCheck` behind (floor work; the table stays
   * open). Optional so the pre-order view, which has no bill, need not say so.
   */
  canCheck?: boolean;
  /** Opens the check dialog. Never records or prints directly. */
  onCheck?: () => void;
  /**
   * D146 — may take a DISCOUNT AT THE BILL: `RESTAURANT_INVOICE_VOID`, the
   * rung the server puts `applyBillDiscount`/`removeBillDiscount` behind.
   * Without it the control and the remove are absent; the line still shows.
   */
  canDiscount?: boolean;
  /** Opens the discount sheet. Never sends a command directly. */
  onDiscount?: () => void;
  /** Takes the bill's discount off (a `removeBillDiscount` command). */
  onDiscountRemove?: () => void;
  /**
   * Founder, 2026-10-07: the cashier ticks sent lines and prints a bill for
   * just those, or settles just those. Each is offered only when the page
   * passes it (the session may print, may settle).
   */
  onPrintItems?: (plateIds: string[]) => void;
  onSettleItems?: (plateIds: string[]) => void;
  /** The store's zone, for the check note's clock (the paper's own rule). */
  storeTimezone?: string | null;
  /**
   * PAPER head (founder, 2026-10-03) — the cover count as a quiet button under
   * the head's Move action: "Guests: 4", or "Guests" when nobody counted (D45:
   * blank is a real answer). `onOpen` opens the page's own `GuestCountDialog`;
   * without it the count is a read-out (a closed bill's covers are a fact).
   * The page decides everything — the channel (off-premise bills have no
   * count and pass nothing), the rung, the state — and passes `undefined` for
   * "no row". Ignored outside PAPER, where the page keeps its own covers row.
   */
  headGuests?: {
    count: number | null;
    onOpen?: () => void;
    disabled?: boolean;
  };
  /**
   * D153 — «الكابتن» on a dine-in bill's head, in BOTH tills: the captain's
   * first name, or «من غير كابتن». `onOpen` makes it a control (the page
   * passes it only to an `INVOICE_CLOSE` holder on an OPEN bill); without it
   * it is a read-out. The page passes `undefined` off DINE_IN — a bag has no
   * captain, so no row.
   */
  headCaptain?: {
    name: string | null;
    onOpen?: () => void;
    disabled?: boolean;
  };
  /**
   * B6 · P26 (plan D-46) — PAPER, a bill at no table: the design's Delivery /
   * Pickup head (`OffPremiseOrderHead`) in place of the table's. `contact` is
   * given for an OWN delivery only: its name, phone and address are edited
   * inline there, so the read-only customer block below is not drawn for it.
   * Ignored outside PAPER.
   */
  offPremiseHead?: {
    channel: "DELIVERY" | "TAKEAWAY";
    platformName: string | null;
    onSwitch?: () => void;
    contact?: OffPremiseHeadContact;
  };
  /**
   * 2026-10-20 — an own delivery's dispatch from the bill (`BillDispatchStrip`):
   * in the PAPER head under the contact fields, and at the foot of the classic
   * till's customer block. The page builds it (who may, which bill).
   */
  billDispatch?: { head: ReactNode; body: ReactNode };
  /**
   * 2026-10-20 — the close's words when they are not «إغلاق الفاتورة»: an own
   * delivery whose driver accepted it is HANDED OVER by the close (T16), so the
   * page passes «تسليم إلى المندوب» (the name is on the dispatch line). The act and its gate are unchanged.
   */
  handOver?: boolean;
  /** 2026-10-20 — an own delivery's foot while no driver has accepted it (see the type). */
  dispatchFoot?: {
    /**
     * "assign": nobody holds the bag — the close's slot is «إسناد إلى مندوب»
     * (the Delivery tab's panel) and the close a small secondary beside it,
     * for a bag leaving without an app driver (typed rider, the customer).
     * "offered": an offer is out — no primary; the head's chip counts down,
     * and the small close stays.
     */
    kind: "assign" | "offered";
    onAssign: () => void;
    assignDisabled: boolean;
    /** Why the assign is greyed, said under it (null: the head already says it). */
    assignReason: string | null;
    /** The id of the line that says why (the head's contact line, or this one). */
    describedBy?: string;
    /** The greyed assign's tooltip: why, in words. */
    assignTitle?: string | null;
  };
}) {
  const t = useTranslate();
  const money = useMoney();
  /* `??` rather than a required prop: a bill served by a release with no refund
     event must read as "nothing to reprint", never crash the till. The label
     rule itself is `refundSlipButtons`', not this component's. */
  const refundSlips = refundReceipts ?? [];
  const localName = useLocalName();

  /**
   * A plate's sub-labels in the language the till is in.
   *
   * The dish NAME already went through `useLocalName`; the extras, the combo
   * components and the swap did not, so an English till read "Margherita pizza
   * Large / + جبنة زيادة / 175 EGP" — the price explained in a language the
   * person being asked about it cannot read. `groupPlates` carries both halves
   * of all three (the receipt prints both), and this picks, with Arabic as the
   * fallback for the same reason `useLocalName` has it: `nameEn` is optional and
   * frequently blank, and an empty sub-line is worse than one in the other
   * language.
   */
  const localLabel = (ar: string | null, en: string | null): string | null =>
    ar === null ? null : localName({ nameAr: ar, nameEn: en });


  const isOpen = invoice?.status === "OPEN";
  const isSettled = invoice?.status === "SETTLED";
  /*
   * D31 — the bill left the floor unpaid. The overlay below is THREE-WAY, not
   * "CARD else CASH": a binary read labelled a walk-out "cash", so the one
   * outcome the cashier most needs to see confirmed looked exactly like money
   * in the drawer. It also drops the success tone — a green tick over "nobody
   * paid" is the wrong sentence in the wrong colour.
   */
  const isWriteOff = invoice?.settlementMethod === "WRITE_OFF";
  /*
   * A cancelled bill is not a bill. It used to render exactly like a live one —
   * running total, close/settle footer, no word anywhere saying the thing had
   * been called off — so the only way to tell was that every button bounced.
   * It gets the same panel overlay the settled state gets, in the destructive
   * tone, with the two things still true of it: it can be printed for the
   * record, and the floor is where the cashier goes next.
   */
  const isCancelled = invoice?.status === "CANCELLED";
  // What the cashier hands back. `settlementAmount` is what was tendered, and
  // it is 0 on a write-off, so this stays 0 there by construction.
  const changeDue = invoice
    ? Math.max(0, (invoice.settlementAmount ?? invoice.total) - invoice.total)
    : 0;
  /*
   * Plates are grouped for DISPLAY only (D15). Three teas are three rows in the
   * database — which is what lets one of them be voided — and one row here with
   * a count, which is what makes the panel readable.
   */
  const sentGroups = groupPlates(sentItems, money);
  const draftGroups = groupPlates(drafts, money);
  // The PAPER panel (2026-10 redesign) on a light till: the table as a badge
  // and a name, and the bill in two labelled sections. Behaviour is unchanged.
  const paper = usePaperTill();
  /** PAPER: the line whose actions sheet is open (tap a line to open). */
  const [actionLine, setActionLine] = useState<{
    group: PlateGroup;
    /** A NEW line can still be edited (size, add-ons); a sent one cannot. */
    kind: "sent" | "draft";
  } | null>(null);
  /*
   * PAPER — the plates the model can call PAID: those of a party that is
   * square (`paidPlateIds`), which is what the Settle window's "by item" makes.
   * A typed part-payment names no plate, so it tags none. Not on a settled
   * bill, whose overlay already says so.
   */
  const paidIds =
    paper && invoice && invoice.status !== "SETTLED"
      ? paidPlateIds(invoice)
      : null;
  const paidCount = (group: PlateGroup) =>
    paidIds ? group.plates.filter((plate) => paidIds.has(plate.id)).length : 0;
  /*
   * PICKED LINES (founder, 2026-10-07): sent, charged plates not yet paid for,
   * ticked to print or settle just those. Cleared when the bill changes hands.
   */
  const canPick =
    paper &&
    invoice !== null &&
    invoice.status !== "SETTLED" &&
    invoice.status !== "CANCELLED" &&
    Boolean(onPrintItems || onSettleItems);
  const pickable = new Set(
    canPick && invoice
      ? markablePlates(invoice)
          .filter((plate) => !(paidIds?.has(plate.id) ?? false))
          .map((plate) => plate.id)
      : [],
  );
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [pickedFor, setPickedFor] = useState(invoice?.id ?? null);
  if ((invoice?.id ?? null) !== pickedFor) {
    setPickedFor(invoice?.id ?? null);
    setPicked(new Set());
  }
  const livePicked = [...picked].filter((id) => pickable.has(id));
  /*
   * The foot's OWN print and Settle act on the ticked lines when there are any,
   * and on the whole bill when none are (founder, 2026-10-07: "no checkbox
   * means all") — no second pair of buttons.
   */
  const printPicked =
    livePicked.length > 0 && onPrintItems ? () => onPrintItems(livePicked) : null;
  const settlePicked =
    livePicked.length > 0 && onSettleItems ? () => onSettleItems(livePicked) : null;
  const groupPickable = (group: PlateGroup) =>
    group.plates.map((plate) => plate.id).filter((id) => pickable.has(id));
  const togglePick = (ids: string[], on: boolean) =>
    setPicked((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  /*
   * PAPER (founder, 2026-10-03): the panel offers no "New bill" any more —
   * "remove the new bill totally". It used to sit in this head (`?new=1`).
   */
  /*
   * PAPER head's Move (founder, 2026-10-03): exactly the gate the old switcher
   * block put on its transfer link — an OPEN bill that sits at a table, a
   * session holding the rung, and another table to move to.
   */
  const headMove =
    paper &&
    isOpen &&
    canTransfer &&
    tables.length > 1 &&
    invoice !== null &&
    invoice.tableIds.length > 0;
  /*
   * D153 — the captain line, one element for both heads. A button only when
   * the page says this session may change it; the text is the same either
   * way, so a waiter reads the same «الكابتن: محمد» the cashier can tap.
   */
  const captainText = headCaptain
    ? headCaptain.name !== null
      ? t((m) => m.captain.line, { name: captainFirstName(headCaptain.name) })
      : t((m) => m.captain.none)
    : null;
  const captainLine = headCaptain ? (
    headCaptain.onOpen ? (
      <button
        type="button"
        disabled={headCaptain.disabled}
        data-testid="order-head-captain"
        onClick={headCaptain.onOpen}
        title={t((m) => m.captain.change)}
        aria-label={`${t((m) => m.captain.change)} — ${captainText}`}
        className="truncate text-start text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
      >
        <bdi>{captainText}</bdi>
      </button>
    ) : (
      <span
        data-testid="order-head-captain-readonly"
        className="truncate text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
      >
        <bdi>{captainText}</bdi>
      </span>
    )
  ) : null;
  const headGuestsLabel =
    headGuests && headGuests.count !== null
      ? t((m) => m.paperFloor.headGuests, { number: headGuests.count })
      : t((m) => m.pos.guestCountLabel);
  /*
   * Every figure in the foot, from the ONE helper the narrow-width summary bar
   * also reads (`tillTotals`). Both buckets price through the same per-plate
   * function the rows use, so the rows always add up to the foot — and the bar
   * floating under this panel can never quote a different total than the panel
   * it belongs to.
   */
  const {
    draftTotal,
    sentTotal,
    serviceChargeTotal,
    vatTotal,
    runningTotal,
    lineCount,
    billDiscountGross,
  } = tillTotals({
    invoiceTotal: invoice?.total ?? 0,
    status: invoice?.status ?? null,
    sentItems,
    drafts,
    // The invoice's OWN snapshot (D33) — never the store's current settings.
    charges: invoice ? invoiceChargeRates(invoice) : null,
    storedCharges: invoice ?? undefined,
    // F2 — already on the invoice and charged at close, so it belongs in the
    // figure quoted while the order is still being built.
    deliveryFee: invoice?.deliveryFee,
    deliveryFeeVat: invoice?.deliveryFeeVat,
    // D146 — the discount at the bill: live on an OPEN bill, stored after.
    billDiscount: invoice ? billDiscountPricingOf(invoice) : null,
    storedBillDiscountGross: invoice ? storedBillDiscountLine(invoice) : 0,
  });
  /*
   * The subtotal/discount rows follow the SAME status rule as the money above
   * them: stored columns on a finished bill, live from the plates while OPEN.
   * On a reopened bill the stored subtotal/discount are the OLD close's, and
   * rendering them beside a live-recomputed total made the block fail its own
   * "must visibly sum" rule the moment a plate moved after reopen.
   */
  const liveTotals =
    invoice && invoice.status === "OPEN" ? liveOpenTotals(invoice) : null;
  /*
   * ROUND 5 — THE PRE-BILL CHECK. Offered on an OPEN bill with something on
   * it, to whoever holds the close rung. Whether the check in the guests' hands
   * is still the bill is the floor tile's to say now, not a note in this foot.
   */
  const { locale } = useI18n();
  /** The totals' breakdown, folded behind the running total until asked for. */
  const [sumsOpen, setSumsOpen] = useState(false);
  /** The small time beside a status group's name (founder, 2026-10-06). */
  const sectionStamp = (
    items: readonly InvoiceItem[],
    state: InvoiceItem["fulfillmentState"],
  ) => {
    const at = sectionStartedAt(items, state);
    return at ? (
      <span
        data-ui="order-section-time"
        data-testid={`order-section-time-${state}`}
        className="ms-1.5 text-[length:calc(var(--font-size-2xs)_-_0.5px)] font-semibold tabular-nums text-[color:var(--text-muted)]"
      >
        {formatSectionStamp(at, storeTimezone, locale === "en" ? "en" : "ar")}
      </span>
    ) : null;
  };
  const showCheck = Boolean(
    canCheck && onCheck && liveTotals && liveTotals.total > 0,
  );
  const displaySubtotal = liveTotals
    ? liveTotals.subtotal
    : invoice?.subtotal || sentTotal;
  const displayDiscount = liveTotals
    ? liveTotals.discountTotal
    : (invoice?.discountTotal ?? 0);
  /*
   * Which button this screen wants next, from the same pure rule the summary
   * bar applies. It decides one thing here — whether Close is the primary or
   * the outline beside Send — and that is exactly the decision that must not
   * be made twice.
   */
  /*
   * THE SPLIT STATE left the foot (user, 2026-10-20): the bill no longer
   * carries a «تقسيم الفاتورة» row. The split lives behind the printer — the
   * page's `PrintChoiceSheet` lists the parts, their shares and the unclaimed
   * plates — so it is not read here.
   */
  // B89. Zero on every ordinary bill, which is what keeps the block below out
  // of the way until it is the only thing that matters.
  // B102 — who an off-premise bill is for, through the shared helper so the
  // panel, the receipt and the back office cannot name a customer three ways.
  const customerLines = invoice ? billCustomerLines(invoice) : [];
  // T11 — which of phone / address is missing, so the warning names it.
  const undeliverable = invoice ? undeliverableReason(invoice) : null;
  const overpaid = invoice ? overpaidOnBill(invoice) : 0;

  const primary = invoice
    ? tillPrimaryAction({
        status: invoice.status,
        draftCount: drafts.length,
        canFire,
        canSettle,
        canRequestCashier: canAppend && Boolean(onRequestCashier),
      })
    : null;
  /*
   * The D22 close gate, read rather than re-spelled (UX-round D15, ruling E1).
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * This foot used to carry its own copy — `!canClose || isBusy ||
   * <blocking> > 0` — beside a summary bar reading the same rule out of
   * `tillActions`. Two spellings of one gate is how they drift; `isBusy` stays
   * here because it is this screen's own state, which is the line that
   * function's docblock draws.
   */
  const close = invoice
    ? tillCloseAction({
        status: invoice.status,
        blocking: closeBlocking,
        canClose,
      })
    : null;
  /* 2026-10-20 — a greyed close says why in its tooltip and description, not a line. */
  /*
   * 2026-10-20 (user) — «طباعة التذكرة» lives behind the printer: while an
   * unprinted ticket holds the close, the printer wears an attention dot and
   * names the reason, and the greyed close's reason points at it.
   */
  const ticketOnPrinter = isOpen && closeUnblock === "resendTicket" && Boolean(onCheck);
  const closeReasonText = useCloseBlockedText(close?.reason ?? null, handOver, ticketOnPrinter);
  const closeReasonTitle = close && !close.enabled && closeReasonText ? closeReasonText : undefined;
  /*
   * T6 / D173 — the greyed Close's reason (hidden) and its one tap. Not where
   * the foot offers Settle in Close's place (PAPER's window, the dark direct
   * takeaway): Settle does not wait for the kitchen (D126), and the PAPER
   * window carries the same note under its keep-open line. The dark-only
   * `closeNote` keeps its own sentence.
   */
  const closeBlockedNote =
    isOpen && !closeNote && close?.reason && closeAs !== "settle" && !(paper && onSettleOpen) ? (
      <CloseBlockedNote
        reason={close.reason}
        action={closeUnblock}
        onAction={onCloseUnblock}
        isBusy={isBusy}
        isLoading={busyKey === "close-unblock"}
        surface="panel"
        handOver={handOver}
      />
    ) : null;

  /** One fired row (the same row in every look; PAPER wraps it in its fire). */
  const renderSent = (group: PlateGroup) => {
            const gone = group.fulfillmentState === "VOIDED";
            const comped = group.billingState === "COMPED";
            /*
             * Editing rules by state (D18's edit matrix). A plate the kitchen
             * has started is the cashier's to change, not the waiter's — which
             * is why minus opens the void dialog rather than removing a row.
             */
            const queued = group.fulfillmentState === "QUEUED";
            // The plate's next step on the pass, for the PAPER row's one chip.
            // A role that cannot mark Ready (a waiter) goes straight to Serve
            // while the plate cooks, as the sheet below already allows.
            const nextStep: { key: string; label: string; run: () => void } | null =
              canStartLine && queued
                ? { key: "start", label: t((m) => m.kitchen.actionStartShort), run: () => onStartPreparing(group) }
                : canPrepare && group.fulfillmentState === "PREPARING"
                  ? { key: "ready", label: t((m) => m.kitchen.actionReady), run: () => onMarkReady(group) }
                  : canServe &&
                      (group.fulfillmentState === "PREPARING" || group.fulfillmentState === "READY")
                    ? { key: "serve", label: t((m) => m.pos.serveAction), run: () => onServe(group) }
                    : null;
            const pickIds = groupPickable(group);
            return (
              <LineRow
                key={group.key}
                kind="sent"
                thumb={paper ? dishThumb?.(group.productId) : undefined}
                testId={`line-${group.key}`}
                leading={
                  pickIds.length > 0 ? (
                    <Checkbox
                      size="md"
                      checked={pickIds.every((id) => picked.has(id))}
                      onChange={(event) => togglePick(pickIds, event.target.checked)}
                      aria-label={t((m) => m.pos.pickItem)}
                      data-testid={`pick-${group.key}`}
                    />
                  ) : undefined
                }
                name={localName({
                  nameAr: group.plateNameAr,
                  nameEn: group.plateNameEn,
                })}
                unit={plateUnit(group)}
                priceOverride={group.unitPriceOverride !== null}
                addonLabel={localLabel(group.addonLabel, group.addonLabelEn)}
                substitutionLabel={localLabel(
                  group.substitutionLabel,
                  group.substitutionLabelEn,
                )}
                comboLabel={localLabel(group.comboLabel, group.comboLabelEn)}
                instruction={group.note}
                /*
                 * R12/§10 — WHICH plate came back, on the till too.
                 *
                 * The settled overlay printed the group at full quantity with
                 * only a total refund figure beneath it, so a cashier
                 * reprinting a partly refunded bill could not tell which plate
                 * was out — while the back-office detail marked each one. The
                 * grouping key carries `refunded` now, so a refunded plate is
                 * its own row and can say so. Below void and comp in
                 * precedence: those say the plate was never charged at all,
                 * which is a different (and prior) fact to money going back.
                 */
                /*
                 * A REMAKE IS NOT A PRIOR FACT (B117/A1).
                 *
                 * The first three arms are: void, comp and refunded each say
                 * this plate was never charged, or has been paid back, and none
                 * of them can be undone by the kitchen. `remakeCount` sat with
                 * them and it does not belong there — a remade plate is still
                 * COOKING, and it is the plate the guest is actually waiting
                 * for. Ranking the badge above the state hid it PERMANENTLY:
                 * any plate with `remake_count > 0` showed «إعادة تحضير ×1» for
                 * the rest of its life and never showed QUEUED or «بيتحضّر»
                 * again.
                 *
                 * Measured: a bill held a QUEUED pizza with `remake_count 1`
                 * beside a PREPARING pasta. The close button was disabled and
                 * said «في أصناف مستنية عند المطبخ — ابدأها أو اطبع البون أو
                 * شيلها الأول», and the only plate that was waiting showed no
                 * state at all. A refusal naming a line nothing on screen
                 * marks is a screen the cashier cannot act on.
                 *
                 * So the two are printed TOGETHER while the kitchen still has
                 * the plate, state first because that is the live fact and the
                 * remake is the history of it. Once it is SERVED `kitchenNote`
                 * returns null — "served" is the expected end and saying so on
                 * every finished line is noise — and the remake badge stands
                 * alone again, which is exactly where it reads correctly.
                 */
                note={
                  /*
                   * PO-9 (phase 12): a voided or refunded plate says where its
                   * cooked food went — «مرتجع من المطبخ» or «هالك» — the
                   * cashier's own answer, read back off the ledger. On PAPER
                   * the section head already says «ملغى», so the line carries
                   * the fate alone; elsewhere it rides after the state.
                   */
                  gone && group.foodFate
                    ? paper
                      ? foodFateLabel(t, group.foodFate)
                      : `${t((m) => m.pos.void)} — ${foodFateLabel(t, group.foodFate)}`
                    : !gone && group.refunded && group.foodFate
                      ? `${t((m) => m.invoices.itemRefundedChip)} — ${foodFateLabel(t, group.foodFate)}`
                  // PAPER: the section's head names the state (waiting,
                  // cooking, ready, served, cancelled) — repeating it on every
                  // line was noise (founder, 2026-10-06).
                  : paper && (gone || (!comped && !group.refunded && paidCount(group) === 0))
                    ? null
                    : gone
                    ? t((m) => m.pos.void)
                    : comped
                      ? t((m) => m.pos.comp)
                      : group.refunded
                        ? t((m) => m.invoices.itemRefundedChip)
                        : paidCount(group) > 0
                          ? paidCount(group) === group.quantity
                            ? t((m) => m.paperFloor.paid)
                            : `${paidCount(group)}/${group.quantity} ${t((m) => m.paperFloor.paid)}`
                          : plateNote(t, group.fulfillmentState, group.remakeCount)
                }
                noteTone={
                  gone || group.refunded
                    ? "danger"
                    : comped
                      ? "warning"
                      : paidCount(group) > 0
                        ? "success"
                        : "info"
                }
                quantity={group.quantity}
                total={money(group.total)}
                disabled={!isOpen || isBusy || gone}
                onPlus={canAppend ? () => onSentPlus(group) : undefined}
                // Once sent, taking a plate off is a VOID — the cashier's, with a
                // reason — whether or not the kitchen has started it (owner,
                // 2026-10-11). A waiter's minus is gone; the cashier's opens the
                // void dialog, which asks why.
                onMinus={canVoid ? () => onSentVoid(group, false) : undefined}
                onRemove={canVoid ? () => onSentVoid(group, false) : undefined}
                // PAPER (founder, 2026-10-06): ONE action on the row — the
                // plate's NEXT step on the pass (Start → Ready → Serve,
                // founder 2026-10-27) — and every other act (one more / less,
                // swap, remake, comp, print, cancel) in the sheet a tap on
                // the line opens.
                compact={paper}
                onOpen={paper && isOpen && !gone ? () => setActionLine({ group, kind: "sent" }) : undefined}
                openTitle={t((m) => m.pos.lineActionsTitle)}
                trailing={
                  paper ? (
                    isOpen && !gone && nextStep ? (
                      <div className="mt-1.5 flex">
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`${nextStep.key}-${group.key}`}
                          onClick={nextStep.run}
                          data-ui="chip"
                          className={
                            nextStep.key === "serve"
                              ? "rounded-full border border-[color:var(--success)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--success)] transition-colors enabled:hover:bg-[color:var(--success-100)] disabled:opacity-40"
                              : "rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                          }
                        >
                          {nextStep.label}
                        </button>
                      </div>
                    ) : null
                  ) : isOpen && !gone ? (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {/* The kitchen's half of the pass, reachable from the
                          till (round 8). The board is still the primary
                          actuator; this is the recovery path for the store
                          whose display-mode board died mid-service, where
                          every QUEUED plate would otherwise be unmovable and
                          D22 would refuse to close the bill. Same permission,
                          same command, same group-wide payload as Serve —
                          plates in a group share a state by construction. */}
                      {canStartLine && group.fulfillmentState === "QUEUED" ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`start-${group.key}`}
                          onClick={() => onStartPreparing(group)}
                          data-ui="chip"
                          className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                        >
                          {t((m) => m.kitchen.actionStartShort)}
                        </button>
                      ) : null}
                      {canPrepare && group.fulfillmentState === "PREPARING" ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`ready-${group.key}`}
                          onClick={() => onMarkReady(group)}
                          data-ui="chip"
                          className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                        >
                          {t((m) => m.kitchen.actionReady)}
                        </button>
                      ) : null}
                      {/* The waiter's half of the pass (D18): food that is
                          cooking or sitting on the pass gets marked delivered
                          from HERE, because the waiter holds ITEM_SERVE and
                          cannot open the kitchen board. Without this chip the
                          permission existed with no surface to use it, and
                          every plate's servedAt came from the close-time
                          collapse instead of a person. */}
                      {canServe &&
                      (group.fulfillmentState === "PREPARING" ||
                        group.fulfillmentState === "READY") ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`serve-${group.key}`}
                          onClick={() => onServe(group)}
                          data-ui="chip"
                          className="rounded-full border border-[color:var(--success)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--success)] transition-colors enabled:hover:bg-[color:var(--success-100)] disabled:opacity-40"
                        >
                          {t((m) => m.pos.serveAction)}
                        </button>
                      ) : null}
                      {/* Reprint, for the chit that got wet. Offered only
                          where paper exists to reprint: the group has to
                          carry a ticket, and the caller only grants canPrint
                          when the store actually prints chits. */}
                      {canPrint && group.plates[0].kitchenTicketId ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`print-${group.key}`}
                          onClick={() =>
                            onPrintChit(
                              group.plates[0].kitchenTicketId as string,
                            )
                          }
                          data-ui="chip"
                          className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                        >
                          {t((m) => m.pos.printChit)}
                        </button>
                      ) : null}
                      {/* B19: the guest changed their mind and the plate has
                          not been cooked yet. Mirrors the server's own rule
                          exactly (B40/D85) — QUEUED always, and PREPARING too
                          where the PRINTER is what moved it there — because a
                          chip that could only ever produce a refusal is worse
                          than no chip, and a waiter who has already told the
                          guest "yes" deserves to see that before they say it.
                          READY and SERVED never: somebody has plated it. */}
                      {canSubstitute &&
                      (group.fulfillmentState === "QUEUED" ||
                        (kitchenPrintsChits &&
                          group.fulfillmentState === "PREPARING")) &&
                      !comped ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          data-testid={`swap-amend-${group.key}`}
                          onClick={() => onAmendSwaps(group)}
                          data-ui="chip"
                          className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                        >
                          {t((m) => m.pos.swapAmend)}
                        </button>
                      ) : null}
                      {/* NOT ON A PLATE THE KITCHEN HAS NOT STARTED (B117/D5).
                          
                          `canRemake` is a PERMISSION — it says nothing about
                          the plate — so the chip was live on a QUEUED line and
                          every tap 404'd. Worse, it 404'd as
                          `RESTAURANT_INVOICE_ITEM_NOT_FOUND`, so the waiter was
                          told the plate had left the bill and to refresh and
                          choose again: eight taps, eight refusals, on a plate
                          sitting right there. The server now names the real
                          reason (`RESTAURANT_ITEM_NOT_STARTED`); this stops the
                          waiter having to meet it, on the swap chip's own rule
                          directly above — a chip that could only ever produce a
                          refusal is worse than no chip.
                          
                          DISABLED rather than hidden, because the control's
                          absence would read as "this plate cannot be remade at
                          all", and a minute later it can. A remake writes
                          `REMAKE_WASTE` for ingredients already used, which is
                          why QUEUED is the line: nobody has cooked it, so there
                          is nothing to waste and nothing to remake. */}
                      {canRemake ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        data-testid={`remake-${group.key}`}
                        /* B117/RT-C — grey, but not deaf. A plate the kitchen
                           has not started cannot be remade (nothing has been
                           cooked, so there is no waste to write), and the chip
                           says so on the tap instead of absorbing it. It keeps
                           the disabled LOOK via `data-unavailable`, so it still
                           reads as unavailable at a glance. */
                        data-unavailable={
                          group.fulfillmentState === "QUEUED" ? "true" : undefined
                        }
                        onClick={() =>
                          group.fulfillmentState === "QUEUED"
                            ? onRemakeBlocked(group)
                            : onRemake(group)
                        }
                        data-ui="chip"
                        className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40 data-[unavailable=true]:opacity-40"
                      >
                        {t((m) => m.pos.remake)}
                      </button>
                      ) : null}
                      {canComp && !comped ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        data-testid={`comp-${group.key}`}
                        onClick={() => onSentVoid(group, true)}
                        data-ui="chip"
                        className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--danger)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.comp)}
                      </button>
                      ) : null}
                    </div>
                  ) : null
                }
              />
            );
          };

  /** One row of the basket — nothing on it has reached the kitchen. */
  /*
   * A plate's price as the sum it is (waiter phone, 2026-10-28): «150 + 35 جنيه»
   * — the dish, then what its options add — so the line total beside the
   * stepper visibly adds up. A plain plate keeps its one figure. The digits sit
   * in one LTR isolate so Arabic does not read «35 + 150».
   */
  const plateUnit = (group: PlateGroup) => {
    const extra =
      group.quantity > 0 ? group.total / group.quantity - group.unitChargedPrice : 0;
    if (!paper || extra < 0.005) return money(group.unitChargedPrice);
    const plain = money(extra).slice(moneyAmount(extra).length);
    return `\u2066${moneyAmount(group.unitChargedPrice)} + ${moneyAmount(extra)}\u2069${plain}`;
  };

  const renderDraft = (group: PlateGroup) => (
            <LineRow
              key={group.key}
              testId={`draft-${group.key}`}
              name={localName({
                nameAr: group.plateNameAr,
                nameEn: group.plateNameEn,
              })}
              unit={plateUnit(group)}
              priceOverride={group.unitPriceOverride !== null}
              addonLabel={localLabel(group.addonLabel, group.addonLabelEn)}
              substitutionLabel={localLabel(
                group.substitutionLabel,
                group.substitutionLabelEn,
              )}
              comboLabel={localLabel(group.comboLabel, group.comboLabelEn)}
              instruction={group.note}
              // PAPER: the section's own head says "not sent yet" once.
              note={paper ? null : t((m) => m.pos.notSentYet)}
              noteTone="warning"
              thumb={paper ? dishThumb?.(group.productId) : undefined}
              quantity={group.quantity}
              // The group's own total, exactly as the fired rows above render
              // it: charged base + addons per plate. Pricing the row off
              // `unitChargedPrice` here dropped every modifier, so a staged
              // plate quoted less than the basket total it was inside.
              total={money(group.total)}
              disabled={isBusy}
              onPlus={() => onDraftPlus(group)}
              onMinus={() => onDraftMinus(group)}
              onRemove={() => onDraftRemove(group)}
              // PAPER (founder, 2026-10-06): the same shape as a sent line —
              // a tap opens the line's acts, led by Edit (size, add-ons), and
              // the row keeps the stepper and one action (Send this).
              compact={paper}
              onOpen={paper && isOpen ? () => setActionLine({ group, kind: "draft" }) : undefined}
              openTitle={t((m) => m.pos.lineActionsTitle)}
              aside={
                // The line's own Send (2026-10-28): a small chef-hat square
                // after the total, not a full-width bar under every line — the
                // bar's «إرسال إلى المطبخ» sends them all, the common case.
                paper && isOpen && canFire && draftGroups.length > 1 ? (
                  <button
                    type="button"
                    data-ui="line-fire"
                    disabled={isBusy}
                    data-testid={`fire-${group.key}`}
                    onClick={() => onFireGroup(group)}
                    aria-label={t((m) => m.pos.fireGroupOnly)}
                    title={t((m) => m.pos.fireGroupOnly)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-[color:var(--border-strong)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                  >
                    <ChefHat size={15} strokeWidth={1.8} aria-hidden="true" />
                  </button>
                ) : undefined
              }
              trailing={
                paper ? null : isOpen ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {/* Coursing without ceremony: fire THIS group now, leave
                        the rest staged. The big send button below still fires
                        everything, which is the common case. */}
                    {canFire && draftGroups.length > 1 ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        data-testid={`fire-${group.key}`}
                        onClick={() => onFireGroup(group)}
                        data-ui="chip"
                        className="rounded-full border border-[color:var(--accent)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--accent)] transition-colors enabled:hover:bg-[color:var(--surface-alt)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.fireGroup)}
                      </button>
                    ) : null}
                    {/* The same amend chip the fired rows carry (B19). A
                        basket plate could always be removed and re-added
                        instead, but two ways to do one thing is how one of
                        them stops being maintained — and the waiter should not
                        have to know which side of the fire line they are on. */}
                    {canSubstitute ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        data-testid={`swap-amend-${group.key}`}
                        onClick={() => onAmendSwaps(group)}
                        data-ui="chip"
                        className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.swapAmend)}
                      </button>
                    ) : null}
                    {canAppend ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        data-testid={`note-${group.key}`}
                        onClick={() => onEditNote(group)}
                        data-ui="chip"
                        className="rounded-full border border-[color:var(--border-strong)] px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.noteChip)}
                      </button>
                    ) : null}
                  </div>
                ) : null
              }
            />
          );

  return (
    <aside
      data-ui="card"
      data-panel="order"
      className={[
        // Three mutually exclusive display/flex sets — never `flex` and
        // `hidden` on one element, which resolves by stylesheet order.
        narrow === "hide"
          ? "hidden lg:flex"
          : narrow === "show"
            ? "flex min-h-0 flex-1"
            : "flex shrink-0",
        // `lg:flex-none` restores the fixed 420px column above the breakpoint
        // for all three, so the desktop two-pane layout is untouched. From
        // 1,280px it grows with the screen (founder, 2026-10-15, F35 → D159);
        // the light till's own width is `_paper-till.css`'s, the same clamp.
        "relative w-full flex-col border-[color:var(--border)] bg-[color:var(--surface)] lg:w-[420px] xl:w-[clamp(420px,28vw,520px)] lg:flex-none lg:border-s",
      ].join(" ")}
      aria-label={t((m) => m.pos.currentOrder)}
    >
      {paper && offPremiseHead ? (
        <OffPremiseOrderHead
          numberLine={
            <>
              <span data-ui="order-head-number" className="truncate">
                {invoice
                  ? t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })
                  : t((m) => m.pos.tableFree)}
              </span>
              <span data-ui="order-head-meta" className="shrink-0">
                {t(
                  countedKey(lineCount, {
                    one: (m) => m.pos.lineCountOne,
                    two: (m) => m.pos.lineCountTwo,
                    few: (m) => m.pos.lineCountFew,
                    many: (m) => m.pos.lineCount,
                  }),
                  { count: lineCount },
                )}
              </span>
            </>
          }
          channel={offPremiseHead.channel}
          platformName={offPremiseHead.platformName}
          onSwitch={offPremiseHead.onSwitch}
          switchDisabled={isBusy}
          contact={offPremiseHead.contact}
          orderedBy={invoice?.orderedBy ?? null}
          dispatch={billDispatch?.body}
          dispatchHead={billDispatch?.head}
          extra={
            /* T6 — the fee and platform correction stays reachable for an own
               delivery, whose customer block is replaced by the fields. */
            offPremiseHead.contact && canEditCharges ? (
              <button
                type="button"
                disabled={isBusy}
                data-ui="order-head-link"
                data-kind="charges"
                data-testid="invoice-charges-edit"
                onClick={onEditCharges}
                className="self-start disabled:opacity-40"
              >
                {t((m) => m.pos.chargesEdit)}
              </button>
            ) : undefined
          }
        />
      ) : paper ? (
        /* The redesign's head: the bill's number and its count on the first
           line, the table's badge and name under it. */
        <div data-ui="order-head" className="flex shrink-0 items-center gap-2.5">
          {/* ONE compact row (founder, 2026-10-07): the table's badge, its name
              with "Invoice #N · N items" small under it, and Move and the
              covers as two icon buttons whose tooltip says what they do. */}
          <DoodleBadge id="cutlery" size={28} animate={false} />
          <div className="min-w-0 flex-1">
            <div data-ui="order-head-title" className="truncate">
              <bdi>{tableName ?? bindLabel}</bdi>
            </div>
            <div data-ui="order-head-row" className="flex min-w-0 items-baseline gap-1.5">
              <span data-ui="order-head-number" className="truncate">
                {invoice
                  ? t((m) => m.pos.invoiceNumber, {
                      sequence: invoice.dailySequence,
                    })
                  : t((m) => m.pos.tableFree)}
              </span>
              {invoice ? (
                <span data-ui="order-head-meta" className="shrink-0">
                  {"· "}
                  {t(
                    countedKey(lineCount, {
                      one: (m) => m.pos.lineCountOne,
                      two: (m) => m.pos.lineCountTwo,
                      few: (m) => m.pos.lineCountFew,
                      many: (m) => m.pos.lineCount,
                    }),
                    { count: lineCount },
                  )}
                </span>
              ) : null}
            </div>
            {/* No captain on the phone (founder, 2026-10-28: "no need for the
                captain name anywhere"). */}
            {captainLine ? <div className="flex min-w-0 max-lg:hidden">{captainLine}</div> : null}
          </div>
          {headMove || headGuests ? (
            <div data-ui="order-head-actions" className="flex shrink-0 items-center gap-1">
              {headGuests ? (
                headGuests.onOpen ? (
                  <button
                    type="button"
                    disabled={headGuests.disabled}
                    data-ui="order-head-icon"
                    data-testid="order-head-guests"
                    onClick={headGuests.onOpen}
                    title={headGuestsLabel}
                    aria-label={headGuestsLabel}
                    className="tabular-nums disabled:opacity-40"
                  >
                    <Users size={16} strokeWidth={1.8} aria-hidden="true" />
                    {paper ? <span>{t((m) => m.pos.headGuestsShort)}</span> : null}
                    {headGuests.count !== null ? <span>{headGuests.count}</span> : null}
                  </button>
                ) : (
                  /* A count nobody here may change is a fact, not a control. */
                  <span
                    data-ui="order-head-icon"
                    data-readonly=""
                    data-testid="order-head-guests-readonly"
                    title={headGuestsLabel}
                    aria-label={headGuestsLabel}
                    className="tabular-nums"
                  >
                    <Users size={16} strokeWidth={1.8} aria-hidden="true" />
                    {headGuests.count !== null ? <span>{headGuests.count}</span> : null}
                  </span>
                )
              ) : null}
              {headMove ? (
                <button
                  type="button"
                  disabled={isBusy}
                  data-ui="order-head-icon"
                  data-testid="invoice-transfer"
                  onClick={onTransfer}
                  title={t((m) => m.pos.transferTable)}
                  /* PAPER shows the words, which are then its name. */
                  aria-label={paper ? undefined : t((m) => m.pos.transferTable)}
                  className="disabled:opacity-40"
                >
                  <ArrowLeftRight size={16} strokeWidth={1.8} aria-hidden="true" />
                  {/* Words beside the icon (waiter phone, 2026-10-28): two bare
                      icons in the head were guesses. A part of the name, so
                      the visible label stays inside the accessible one. */}
                  {paper ? <span>{t((m) => m.pos.headTransferShort)}</span> : null}
                </button>
              ) : null}
            </div>
          ) : null}
          {/* The phone head line ends with the bell and sign-out (2026-10-28):
              below `lg` the page's top bar is not shown. */}
          <TillPhoneControls rule />
        </div>
      ) : null}
      <div className={paper ? "hidden" : "flex shrink-0 items-center gap-2 px-4 pb-2.5 pt-3.5"}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-extrabold tracking-[-0.01em]">
            {t((m) => m.pos.currentOrder)}
          </div>
          <div className="mt-px text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
            {invoice
              ? t((m) => m.pos.invoiceNumber, {
                  sequence: invoice.dailySequence,
                })
              : t((m) => m.pos.tableFree)}
            {" · "}
            {/* ISOLATED. An Arabic table name inside an English (LTR)
                paragraph drags what follows it: this line painted as
                «Invoice #2 · 3 · الصالة 2 items», with the item count torn
                off its own noun and parked inside the table's name. The same
                rule every chip in this feature already follows — it was
                simply never applied to the line that names the bill. */}
            <bdi>{tableName ?? bindLabel}</bdi>
            {invoice ? (
              <>
                {" · "}
                {t(
  // B117/A2 — the DUAL. `plural.ts` holds the rule; this call site
  // was still spelling one form for every number.
  countedKey(lineCount, {
    one: (m) => m.pos.lineCountOne,
    two: (m) => m.pos.lineCountTwo,
    few: (m) => m.pos.lineCountFew,
    many: (m) => m.pos.lineCount,
  }),
  { count: lineCount },
)}
              </>
            ) : null}
          </div>
          {/* Not under PAPER, where this head is hidden and the paper head
              above carries the same line. */}
          {captainLine && !paper ? (
            <div className="mt-0.5 flex min-w-0">{captainLine}</div>
          ) : null}
        </div>
      </div>

      {/* WHO THIS ONE IS FOR (B9, B102) — only on a bill that sits at no table.

          `OffPremiseDialog` asks for a name, a phone and an address, and for
          two days after it shipped none of the three reached ANY screen. A
          delivery cannot be delivered off a bill that will not say where it is
          going, and a cashier who mistypes a phone has no way even to notice.

          Rendered whenever the bill is off-premise, INCLUDING when nothing was
          typed: "nobody recorded who this is for" is the state a cashier has to
          be able to see before the food is bagged, and a block that vanished
          when empty would hide exactly the case that matters. */}
      {invoice && invoice.channel !== "DINE_IN" && !(paper && offPremiseHead?.contact) ? (
        <div className="shrink-0 px-4 pb-2.5" data-testid="invoice-customer">
          <div className="mb-1 flex items-center justify-between">
            <span data-ui="section-label"
              className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.pos.customerBlockTitle)}
            </span>
            {/* THE CORRECTION, beside the thing it corrects (B107).

                A waiter who mistyped a phone number must find the fix where
                they read the mistake, not in a different verb on a different
                screen — the same rule the split sheet's "clear the mark" undo
                follows. Offered while the bill is OPEN or CLOSED, which is
                where the command lands: the cheque being printed is exactly
                when the customer rings back about the flat number. */}
            <span className="flex items-center gap-3">
              {/* T6 — the OTHER correction, beside it: the fee, the platform
                  and the channel. Its own control rather than three more boxes
                  on the customer sheet, because this one moves the total and
                  needs the cashier's rung, not the waiter's. */}
              {canEditCharges ? (
                <button
                  type="button"
                  disabled={isBusy}
                  data-testid="invoice-charges-edit"
                  onClick={onEditCharges}
                  data-ui="section-link"
                  className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
                >
                  {t((m) => m.pos.chargesEdit)}
                </button>
              ) : null}
              {canEditCustomer ? (
                <button
                  type="button"
                  disabled={isBusy}
                  data-testid="invoice-customer-edit"
                  onClick={onEditCustomer}
                  data-ui="section-link"
                  className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
                >
                  {t((m) => m.pos.customerEdit)}
                </button>
              ) : null}
            </span>
          </div>
          {/* T7/T13 — WHO BROUGHT THE ORDER, and its number on their side: the
              cashier persona found the bill saying «طلب للاستلام» and nothing
              else about a Talabat order whose courier was reading TLB-48213
              off his phone. Isolated segments, for the chit band's reason. */}
          {invoice?.aggregatorId ? (
            <p
              className="mb-1 flex flex-wrap items-center gap-1.5 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold"
              data-testid="invoice-platform"
            >
              <span data-ui="chip" data-tone="info">
                <bdi>{platformName ?? t((m) => m.pos.aggregatorMethod)}</bdi>
              </span>
              {invoice.platformOrderRef ? (
                <span dir="ltr" className="font-mono text-[length:var(--font-size-2xs)]" data-testid="invoice-platform-ref">
                  {invoice.platformOrderRef}
                </span>
              ) : null}
            </p>
          ) : null}
          {customerLines.length === 0 ? (
            <p className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
              {t((m) => m.pos.customerNone)}
            </p>
          ) : (
            <dl className="flex flex-col gap-0.5">
              {customerLines.map((line) => (
                <div key={line.key} className="flex gap-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)]">
                  <dt className="shrink-0 font-bold text-[color:var(--text-muted)]">
                    {line.key === "name"
                      ? t((m) => m.pos.customerName)
                      : line.key === "phone"
                        ? t((m) => m.pos.customerPhone)
                        : t((m) => m.pos.customerAddress)}
                  </dt>
                  {/* The phone LTR whatever the locale: a number typed into an
                      RTL run reorders its own digits on screen, and this one is
                      read aloud to a rider. */}
                  <dd
                    className="min-w-0 break-words font-semibold"
                    dir={line.key === "phone" ? "ltr" : "auto"}
                    data-testid={`invoice-customer-${line.key}`}
                  >
                    {line.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {/* B7 — «طلبه»: the lines above are the RECIPIENT's; this names
              who ordered it, phone masked. */}
          {invoice.orderedBy ? (
            <OrderedByLine
              orderedBy={invoice.orderedBy}
              testId="invoice-customer-ordered-by"
              className="mt-0.5 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]"
            />
          ) : null}
          {undeliverable ? (
            <p
              role="alert"
              className="mt-1.5 rounded-[var(--radius-list)] border border-[color:var(--warning)] px-2.5 py-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
              data-testid="invoice-undeliverable"
              data-reason={undeliverable}
            >
              {undeliverableMessage(undeliverable, t)}
            </p>
          ) : null}
          {!(paper && offPremiseHead) && billDispatch ? (
            <div className="mt-2 flex flex-col gap-1.5">
              {billDispatch.head}
              {billDispatch.body}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* THE TABLE SWITCHER — only on a bill that HAS a table (F1).

          On a delivery or takeaway bill "switch table" names an act that does
          not exist, and the transfer control inside it offered to move a
          delivery onto a table. `tableIds` empty is the positive claim that
          this bill sits at none, which is exactly what unit 4 made it mean. */}
      {!paper && tables.length > 1 && (!invoice || invoice.tableIds.length > 0) ? (
        <div
          data-ui={paper ? "order-switch" : undefined}
          className="shrink-0 px-4 pb-2.5"
        >
          <div className="mb-1.5 flex items-center justify-between">
            <span data-ui="section-label"
              className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.pos.switchTable)}
            </span>
            {/* The mock's "move", finally honest: a real command exists now.
                Distinct from the chips below, which only NAVIGATE. */}
            {isOpen && canTransfer ? (
              <button
                type="button"
                disabled={isBusy}
                data-testid="invoice-transfer"
                onClick={onTransfer}
                data-ui="section-link"
                className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
              >
                {t((m) => m.pos.transferTable)}
              </button>
            ) : null}
          </div>
          {/* Navigation between tables, which the grid data already supports.
              These chips only NAVIGATE — the mock's "move" is the explicit
              transfer button above, which asks before it acts; a chip that
              silently moved the order elsewhere would be worse than none. */}
          <div
            data-ui="chip-row"
            /* A THIN scrollbar, not the platform's. Left to the default this
               row carried a ~15px bar under ~28px chips — the scrollbar was
               half the height of the thing it scrolled, and on a panel this
               narrow it read as a second row of chrome. `scrollbar-width` for
               Firefox, the pseudo-element for WebKit; both are needed and
               neither covers the other. It stays VISIBLE rather than hidden:
               the row scrolls past the edge and a rail is what says so. */
            className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full"
          >
            {tables.map((table) => {
              const isCurrent = table.tableId === currentTableId;
              return (
                <button
                  key={table.tableId}
                  type="button"
                  aria-current={isCurrent ? "true" : undefined}
                  disabled={isCurrent}
                  data-testid={`panel-table-${table.tableId}`}
                  onClick={() => onPickTable(table)}
                  className={[
                    // The panel weight, not a hook: the border's COLOUR and DASH carry the
                    // table's state (current / occupied / cheque out / free),
                    // and a look rule setting the `border` shorthand would wipe
                    // all four. Only the weight was out of line.
                    "relative shrink-0 rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                    isCurrent
                      ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                      : table.invoiceId
                        ? "border-[color:var(--gold-600)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]"
                        : table.chequeOut
                          ? "border-[color:var(--warning)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]"
                          : "border-dashed border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                  ].join(" ")}
                >
                  {table.name}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* A flex column, so the empty state can GROW into what is left rather
          than claim 100% of the scroller on top of the cards above it — that
          was a scrollbar on a panel with nothing to scroll. */}
      <div
        data-ui={paper ? "order-body" : undefined}
        data-region="order-body"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-2.5 pt-1"
      >
        {conflict}
        {pending}
        {dinerPayment}

        {lineCount === 0 ? (
          <div
            data-ui="empty-bare"
            className={[
              "flex flex-1 flex-col items-center justify-center gap-2.5 py-[30px] text-[color:var(--text-muted)]",
              // An empty TABLE below the desk layout (founder, 2026-10-06):
              // the pricing above and the menu right under it ARE how the
              // order starts, so "Table available / the first dish opens a
              // bill" only pushes the menu down. Kept where it explains why
              // nothing can start (no permission), and on the desk.
              !invoice && canStart ? "max-lg:hidden" : "",
            ].join(" ")}
          >
            <span
              data-ui="empty-icon"
              className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-panel)] border-[length:var(--border-panel-width,1px)] border-[color:var(--border-light)] bg-[color:var(--surface-alt)]"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="9" cy="21" r="1.6" />
                <circle cx="19" cy="21" r="1.6" />
                <path d="M2.5 3h2l2.6 12.4a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.6L21.5 7H6" />
              </svg>
            </span>
            <div className="text-xs font-bold">
              {invoice
                ? t((m) => m.pos.itemsEmptyTitle)
                : t((m) => m.pos.startOrderTitle)}
            </div>
            {invoice ? null : (
              <p className="max-w-[240px] text-center text-[length:var(--font-size-2xs)] font-semibold leading-relaxed text-[color:var(--text-disabled)]">
                {canStart
                  ? t((m) => m.pos.startOrderHint)
                  : t((m) => m.pos.startOrderNeedsPermission)}
              </p>
            )}
          </div>
        ) : null}

        <div className="flex flex-col">
          {paper ? (
            <>
              {/* PAPER (2026-10-03): the design's order — what is NOT sent yet
                  on top, then what is on the table, one block per fire. */}
              {/* The basket as ONE block (waiter phone, 2026-10-28): its own
                  head — «لم يُرسل بعد · 2» and what it adds up to — on an amber
                  ring, so what has not reached the kitchen reads at a glance
                  instead of only through the Send under each line. */}
              {draftGroups.length > 0 ? (
                <section data-ui="order-drafts" data-testid="order-drafts" className="mb-2">
                  <div data-ui="order-drafts-head">
                    <Clock size={14} strokeWidth={2} aria-hidden="true" />
                    <span>
                      {t((m) => m.pos.notSentYet)}
                      {" · "}
                      <span className="tabular-nums">{draftGroups.reduce((sum, group) => sum + group.quantity, 0)}</span>
                    </span>
                    <span className="ms-auto tabular-nums" data-testid="order-drafts-total">
                      {money(draftTotal)}
                    </span>
                  </div>
                  {draftGroups.map(renderDraft)}
                </section>
              ) : null}
              {/* By STATUS, not by round (founder, 2026-10-06): under "Sent
                  to the kitchen" what is waiting, cooking and ready to carry
                  out; under "On the table" what has been served; cancelled
                  plates last. */}
              {paperStatusSections(sentItems, money).map((section, index, all) => (
                <Fragment key={section.state}>
                {index === all.findIndex((entry) => entry.state !== "SERVED" && entry.state !== "VOIDED") &&
                section.state !== "SERVED" && section.state !== "VOIDED" ? (
                  <div data-ui="order-section" data-kind="sent">
                    <ChefHat size={15} strokeWidth={1.8} aria-hidden="true" />
                    {t((m) => m.pos.panelSectionSent)}
                    {/* How many plates the kitchen has (2026-10-28, the design's head). */}
                    <span data-ui="order-section-count" className="tabular-nums">
                      {"· "}
                      {all
                        .filter((entry) => entry.state !== "SERVED" && entry.state !== "VOIDED")
                        .reduce((sum, entry) => sum + entry.count, 0)}
                    </span>
                  </div>
                ) : null}
                {section.state === "SERVED" ? (
                  <div data-ui="order-section" data-kind="table" data-testid="order-section-on-table">
                    <HandPlatter size={15} strokeWidth={1.8} aria-hidden="true" />
                    {/* A bag is not "on the table" (paper persona, 2026-10-07). */}
                    {t((m) =>
                      invoice?.channel && invoice.channel !== "DINE_IN"
                        ? m.pos.panelSectionOnTableOffPremise
                        : m.pos.panelSectionOnTable,
                    )}
                  </div>
                ) : null}
                <div
                  data-ui="order-ticket"
                  data-testid={`order-status-${section.state}`}
                  className="flex flex-col overflow-hidden"
                >
                  <div data-ui="order-ticket-head" className="flex items-center">
                    <span
                      data-ui="order-ticket-state"
                      data-state={section.state === "VOIDED" ? "VOIDED" : section.state}
                      className="flex items-center"
                    >
                      <span aria-hidden="true" data-ui="order-ticket-dot" />
                      {statusSectionLabel(t, section.state)}
                    </span>
                    {sectionStamp(sentItems, section.state)}
                    <span className="flex-1" />
                    <span data-ui="order-ticket-ago">{section.count}</span>
                  </div>
                  {section.groups.map(renderSent)}
                </div>
                </Fragment>
              ))}
            </>
          ) : (
            <>
              {sentGroups.map(renderSent)}
              {/* The basket: the only plates the waiter may edit freely,
                  because nobody has seen them yet (D17). */}
              {draftGroups.map(renderDraft)}
            </>
          )}
        </div>
      </div>

      {/* The artboard's settled state: an overlay covering THIS PANEL ONLY,
          `inset-0` inside its relative column. The menu beside it stays on
          screen and stays readable — the till has not finished working just
          because one bill has, and the next order starts from that same menu.
          An earlier pass unmounted the menu entirely on close, which emptied
          two thirds of the screen and is what this replaces. */}
      {invoice && isSettled ? (
        <div
          className="absolute inset-0 z-10 flex animate-[triRise_.2s_ease] flex-col items-center justify-center gap-3 bg-[color:var(--surface)] p-6"
          role="status"
          data-testid="invoice-settled"
        >
          <span
            className={[
              "flex h-14 w-14 items-center justify-center rounded-full",
              isWriteOff
                ? "bg-[color:var(--danger-100,var(--surface-alt))]"
                : "bg-[color:var(--success-100)]",
            ].join(" ")}
          >
            {isWriteOff ? (
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[color:var(--danger)]"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="m8 8 8 8" />
              </svg>
            ) : (
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[color:var(--success)]"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            )}
          </span>
          <div className="text-center">
            <div
              className={[
                "text-[length:calc(var(--font-size-md)_+_1px)] font-extrabold",
                isWriteOff ? "text-[color:var(--danger)]" : "",
              ].join(" ")}
            >
              {isWriteOff
                ? t((m) => m.pos.writeOffTitle)
                : t((m) => m.pos.paidTitle)}
            </div>
            <div className="mt-[3px] text-xs font-semibold text-[color:var(--text-muted)]">
              {[
                money(invoice.total),
                /*
                 * WHICH METHOD, from the ONE shared helper (D44).
                 *
                 * `null` DROPS THE SEGMENT rather than filling it. A settled
                 * bill always names a method, so a null here is a state this
                 * overlay should not be in — and «اتدفعت — 159.6 جنيه · فاتورة
                 * #3» is an honest omission, where any word chosen for the gap
                 * would be a claim about a bank's money nobody made.
                 *
                 * This was a three-arm ternary DEFAULTING TO CASH, so a bill
                 * settled by bank transfer read «159.6 جنيه · كاش · فاتورة #3»
                 * on the till — directly above the refund button — while the
                 * ledger held one `SETTLEMENT / INSTAPAY / 159.60` and no cash
                 * existed anywhere near the bill. Naming a DIFFERENT real
                 * method is worse than naming none: this is the screen a
                 * cashier reads back when a customer asks how they paid.
                 *
                 * A fourth hardcoded arm would have fixed today's bill and left
                 * the next method to default to cash again, so there is no arm
                 * here at all — the helper is the only place the set is named,
                 * and its spec walks every member of it.
                 */
                /*
                 * B117/B1 — and the plural helper for the same reason the
                 * singular one exists. A bill taken 100.00 on the card then
                 * 123.44 in cash printed «اتدفعت 223.44 جنيه · كاش» right
                 * here, while the Z put 100.00 of it under «بطاقة»: the till
                 * was reading `settlementMethod`, which is the LAST tender's
                 * method and cannot describe a split. Empty `tenders` — a
                 * write-off, a pre-ledger bill — falls back to the single
                 * method, so nothing that read correctly before moves.
                 */
                /* T16 — a platform bill names the PLATFORM here, not the
                   generic word for one (cashier persona, 2026-10-07: «اتدفعت
                   108.30 · التطبيق» on a Talabat bill). The helper stays the
                   one place the set is named; only its generic word is swapped
                   for the name the bill already carries. */
                (() => {
                  const label = tenderMethodsLabel(
                    t,
                    invoice.tenders.map((tender) => tender.method),
                    invoice.settlementMethod,
                  );
                  return label !== null && platformName
                    ? label.split(t((m) => m.pos.aggregatorMethod)).join(platformName)
                    : label;
                })(),
                t((m) => m.pos.invoiceNumber, {
                  sequence: invoice.dailySequence,
                }),
              ]
                .filter((part) => part !== null)
                .join(" · ")}
            </div>
            {changeDue > 0 ? (
              <div className="mt-2 text-[length:var(--font-size-sm)] font-extrabold tabular-nums text-[color:var(--warning)]">
                {t((m) => m.pos.changeDue)} {money(changeDue)}
              </div>
            ) : null}
            {/* D163 — the card/InstaPay reference(s) typed at the settle, so
                the cashier can read back which transfer this was. Only when
                there is one. */}
            {tenderReferencesOf(invoice.tenders).length > 0 ? (
              <div
                className="mt-1 text-xs font-semibold text-[color:var(--text-muted)]"
                data-testid="invoice-settled-reference"
              >
                {t((m) => m.pos.tenderReference)}{" "}
                <bdi dir="ltr">
                  {tenderReferencesOf(invoice.tenders)
                    .map((entry) => entry.reference)
                    .join(" · ")}
                </bdi>
              </div>
            ) : null}
            {/* R12/R5 — what has come back off this bill, BESIDE its figures
                and never inside them: the total above is still exactly what
                the cheque said and the bill is still SETTLED. Only when there
                is one, so an ordinary paid bill keeps its two-line overlay.
                `dir="ltr"` because a leading minus is weak-directional. */}
            {invoice.refundTotal > 0 ? (
              <div
                dir="ltr"
                className="mt-2 text-[length:var(--font-size-sm)] font-extrabold tabular-nums text-[color:var(--danger)]"
                data-testid="invoice-settled-refunded"
              >
                {`${t((m) => m.invoices.refundTotal)} - ${money(invoice.refundTotal)}`}
              </div>
            ) : null}
            {/* B89, ON THE OVERLAY and not only under it.

                The panel renders this notice and its button in the foot; this
                overlay is `inset-0` over that same column, so on a SETTLED
                bill the control was drawn UNDERNEATH it and could not be
                tapped at all. The back office's "open in the till to give
                money back" link led to this very screen, so an over-payment
                that reached a settled bill had no way back to the customer
                through the product.

                A tender can no longer push a bill past its total (the cap in
                `settlePartialInTx`), so this should now be unreachable — which
                is exactly why it is stated here rather than left to be true by
                accident. */}
            {overpaid > 0 ? (
              <div
                className="mt-2 flex flex-col items-center gap-1.5"
                data-testid="invoice-settled-overpaid"
              >
                <span className="text-[length:var(--font-size-xs)] font-bold text-[color:var(--danger)]">
                  {t((m) => m.pos.overpaidNotice, { amount: money(overpaid) })}
                </span>
                {/* `canRefund` AND a bill the command can land on. The dialog
                    behind this button only opens on a CLOSED bill, which is
                    where B89's case lives (a bill is re-closed after the plate
                    is voided), so offering it on a SETTLED one would be a
                    button that does nothing at all — which is what it was. */}
                {canRefund && invoice.status === "CLOSED" ? (
                  <Button
                    variant="danger"
                    size="touch"
                    className="rounded-[var(--radius-list)] font-extrabold"
                    disabled={isBusy}
                    isLoading={busyKey === "overpaid"}
                    data-testid="invoice-settled-overpaid-refund"
                    onClick={onRefundOverpayment}
                  >
                    {t((m) => m.pos.overpaidAction)}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="mt-1.5 flex flex-wrap justify-center gap-2">
            <Button
              variant="primary"
              size="touch"
              className="rounded-[var(--radius-list)] font-extrabold"
              onClick={onNewOrder}
              data-testid="invoice-new-order"
            >
              {t((m) => m.pos.newOrder)}
            </Button>
            <Button
              variant="outline"
              size="touch"
              className="rounded-[var(--radius-list)] font-bold"
              onClick={onBackToTables}
              data-testid="invoice-back-to-tables-settled"
            >
              {t((m) => m.pos.backToTables)}
            </Button>
            <Button
              variant="outline"
              size="touch"
              className="rounded-[var(--radius-list)] font-bold"
              onClick={onPrint}
              data-testid="invoice-print-settled"
            >
              {t((m) => m.pos.print)}
            </Button>
          </div>
          {/* On its OWN row, in the destructive secondary tone — the same rule
              the settle sheet applies to WRITE_OFF. Giving money back is not a
              third way to finish a bill, and a cashier scanning three equal
              pills eventually taps one by rhythm.

              Offered only where the command would land: the rung (R9), a bill
              settled by a method money can go back by (R6 — the shared
              ALLOWLIST cash/card/transfer; a WRITE_OFF took no money, so there
              is none to return, and a method nobody has decided about yet is
              refused rather than defaulted), and a bill worth something
              (a fully-comped bill is refused as `NOTHING_TO_REFUND`).

              The allowlist is the point: a denylist here would offer the
              button for every settlement method the day it is added, and the
              cashier would learn that only after picking plates and
              confirming. */}
          {/* The gate is the REAL condition, not a proxy for it: the ledger
              answers "has this plate already come back" per plate on the
              invoice read (`InvoiceItem.refunded`, R12/§10), so
              `isRefundOfferable` asks whether any plate is still pickable
              rather than whether `refundTotal < total`. The old sum-based test
              agreed with it on today's bills by luck — a fully refunded bill
              whose plates do not sum to its total would have kept offering a
              button the server then refuses. The server is still the gate, and
              its refusal has its own Arabic sentence
              (`RESTAURANT_REFUND_ITEM_ALREADY_REFUNDED`). */}
          {canRefund && isRefundOfferable(invoice) ? (
            <Button
              variant="danger-outline"
              size="touch"
              className="mt-2 rounded-xl font-extrabold"
              onClick={onRefund}
              data-testid="invoice-refund"
            >
              {t((m) => m.pos.refundAction)}
            </Button>
          ) : null}
          {/* ONE BUTTON PER REFUND (trap 9). With a single refund the label is
              the plain one a cashier already knows; with two or more each
              button names its own reference and amount, because two identical
              «اطبع إيصال المرتجع» pills over one bill is a cashier printing
              the wrong slip and finding out from the customer.

              Stacked, one per row, in the same outline tone Print uses: this is
              putting paper in a hand, not giving money back. */}
          {onPrintRefundReceipt
            ? refundSlips.map((slip) => (
                <Button
                  key={slip.commandId}
                  variant="outline"
                  size="touch"
                  className="mt-2 rounded-xl font-bold"
                  onClick={() => onPrintRefundReceipt(slip.commandId)}
                  data-testid={`invoice-print-refund-receipt-${slip.reference}`}
                >
                  {slip.named
                    ? t((m) => m.pos.refundPrintReceiptOne, {
                        reference: slip.reference,
                        amount: money(slip.amount),
                      })
                    : t((m) => m.pos.refundPrintReceipt)}
                </Button>
              ))
            : null}

        </div>
      ) : null}

      {/* The cancelled state, on the same overlay the settled state uses and
          for the same reason: it covers the totals and the action row, which
          are the two things that must not read as a live bill. Print stays —
          a cancelled cheque is still a piece of paper a manager may want. */}
      {invoice && isCancelled ? (
        <div
          className="absolute inset-0 z-10 flex animate-[triRise_.2s_ease] flex-col items-center justify-center gap-3 bg-[color:var(--surface)] p-6"
          role="status"
          data-testid="invoice-cancelled"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--danger-100,var(--surface-alt))]">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[color:var(--danger)]"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M8 8 16 16" />
            </svg>
          </span>
          <div className="text-center">
            <div className="text-[length:calc(var(--font-size-md)_+_1px)] font-extrabold text-[color:var(--danger)]">
              {t((m) => m.pos.cancelledTitle)}
            </div>
            <p className="mx-auto mt-[3px] max-w-[260px] text-xs font-semibold leading-relaxed text-[color:var(--text-muted)]">
              {t((m) => m.pos.cancelledBody)}
            </p>
            <div className="mt-[3px] text-xs font-semibold text-[color:var(--text-muted)]">
              {t((m) => m.pos.invoiceNumber, {
                sequence: invoice.dailySequence,
              })}
            </div>
          </div>
          <div className="mt-1.5 flex gap-2">
            <Button
              variant="primary"
              size="touch"
              className="rounded-[var(--radius-list)] font-extrabold"
              /* Says "back to tables" and now DOES it. It called `onNewOrder`
                 while that happened to navigate to the floor; once that action
                 came to mean "the next party on this table", the label and the
                 behaviour would have parted company. */
              onClick={onBackToTables}
              data-testid="invoice-cancelled-back"
            >
              {t((m) => m.pos.backToTables)}
            </Button>
            <Button
              variant="outline"
              size="touch"
              className="rounded-[var(--radius-list)] font-bold"
              onClick={onPrint}
              data-testid="invoice-print-cancelled"
            >
              {t((m) => m.pos.print)}
            </Button>
          </div>
        </div>
      ) : null}

      {invoice ? (
        /* Below `lg` the bar carries the total and Send (waiter phone,
           2026-10-28): a session that can neither close nor settle has
           nothing else here, and the design draws no foot for it. */
        <div
          data-ui="order-foot"
          className={`flex shrink-0 flex-col gap-2 border-t border-[color:var(--border-light)] px-4 pb-3 pt-2.5${canClose || canSettle ? "" : " max-lg:hidden"}`}
        >
          {livePicked.length > 0 && invoice ? (
            <div
              data-ui="order-picked"
              data-testid="order-picked"
              className="rounded-[var(--radius-list)] bg-[color:var(--surface-alt)] px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2 text-[length:var(--font-size-xs)] font-semibold tabular-nums">
                <span>
                  {t((m) => m.pos.pickedItems, { number: livePicked.length })}
                  {" · "}
                  {money(liveOpenTotals(invoiceForPlates(invoice, livePicked)).total)}
                </span>
                <button
                  type="button"
                  data-testid="order-picked-clear"
                  onClick={() => setPicked(new Set())}
                  className="text-[length:var(--font-size-xs)] font-semibold text-[color:var(--accent)]"
                >
                  {t((m) => m.pos.pickedClear)}
                </button>
              </div>
            </div>
          ) : null}
          <div data-ui="order-sums" className="flex flex-col gap-1 tabular-nums">
            {/* The breakdown folds away behind the total (founder,
                2026-10-06): the total is what the floor reads every time, the
                rows that make it up only when someone asks. The rows stay in
                the DOM, hidden, so a screen reader reaches them through the
                toggle and nothing that reads them loses them. */}
            <div
              id="order-sums-detail"
              data-testid="invoice-sums-detail"
              hidden={!sumsOpen}
              className="flex flex-col gap-1"
            >
            <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
              <span>{t((m) => m.invoices.subtotal)}</span>
              {/* The LIST-price subtotal — what the discount row below is taken
                off. The server's `subtotal` is already net of the tier, so
                showing it here beside the discount read `127 − 28 = 127`. */}
              <span>
                {money(
                  listSubtotal({
                    subtotal: displaySubtotal,
                    discountTotal: displayDiscount,
                  }),
                )}
              </span>
            </div>
            {displayDiscount > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>{t((m) => m.invoices.discountTotal)}</span>
                <span>−{money(displayDiscount)}</span>
              </div>
            ) : null}
            {draftTotal > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--warning)]">
                <span>{t((m) => m.pos.notSentYet)}</span>
                <span>{money(draftTotal)}</span>
              </div>
            ) : null}
            {/* Service charge and VAT (D33). In exclusive mode these ADD onto
              the rows above and the block still visibly sums; in inclusive
              mode the amounts are already inside every price, so each line
              says so ("منها") and adds nothing — a "+" there would state
              arithmetic the total contradicts. */}
            {serviceChargeTotal > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>
                  {t((m) =>
                    invoice.pricesIncludeTax
                      ? m.invoices.serviceChargeIncluded
                      : m.invoices.serviceCharge,
                  )}
                </span>
                <span>{money(serviceChargeTotal)}</span>
              </div>
            ) : null}
            {/* THE DELIVERY FEE (B46), ON THE BILL THE CASHIER IS READING.

                Found by reading the delivery bill in a browser: the foot said
                subtotal 100 and VAT 14 over a total of 136.80, and the 22.80
                between them appeared nowhere. The fee was being charged
                correctly and was INVISIBLE, so a cashier asked "why is it
                136.80?" had no answer on the screen in front of them — which is
                the same "must visibly sum" rule the service-charge and VAT rows
                above exist to keep.

                Its OWN row, never folded into the food lines: `vatTotal` means
                FOOD VAT and the fee's VAT has its own column precisely so the
                courier's tax never spreads over the food. The two are printed
                together here because a diner is quoted one delivery charge, and
                split apart everywhere the money is reported. */}
            {invoice.deliveryFee > 0 || invoice.deliveryFeeVat > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>{t((m) => m.pos.offPremiseFeeLabel)}</span>
                <span data-testid="invoice-delivery-fee">
                  {money(invoice.deliveryFee + invoice.deliveryFeeVat)}
                </span>
              </div>
            ) : null}
            {vatTotal > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>
                  {t((m) =>
                    invoice.pricesIncludeTax
                      ? m.invoices.vatIncluded
                      : m.invoices.vat,
                  )}
                </span>
                <span>{money(vatTotal)}</span>
              </div>
            ) : null}
            </div>
            <button
              type="button"
              data-ui="order-sums-toggle"
              data-testid="invoice-sums-toggle"
              aria-expanded={sumsOpen}
              aria-controls="order-sums-detail"
              onClick={() => setSumsOpen((open) => !open)}
              className={[
                "flex w-full items-baseline justify-between text-start",
                sumsOpen
                  ? "mt-[3px] border-t border-dashed border-[color:var(--border-strong)] pt-1.5"
                  : "",
              ].join(" ")}
            >
              <span className="flex items-center gap-1 text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold">
                {t((m) => m.pos.running)}
                <ChevronDown
                  size={14}
                  strokeWidth={2.2}
                  aria-hidden="true"
                  className={sumsOpen ? "rotate-180 transition-transform" : "transition-transform"}
                />
              </span>
              <span
                className="text-[length:var(--font-size-xl)] font-extrabold tracking-[-0.01em] tabular-nums"
                data-testid="invoice-total"
              >
                {money(runningTotal)}
              </span>
            </button>
            {/* D146 — THE DISCOUNT AT THE BILL, its own line under the total
                and never folded behind the toggle: a gesture the guest was
                promised must be visible to whoever is about to take the money.
                The charges above are already computed on what it left. Remove
                (and the «خصم» control itself) only for the void rung, only on
                an OPEN bill — after the close the paper is in a hand. */}
            {invoice?.billDiscount && billDiscountGross > 0 ? (
              <div
                className="flex items-center justify-between gap-2 text-[length:var(--font-size-xs)] font-bold text-[color:var(--success,var(--text))]"
                data-testid="invoice-bill-discount"
              >
                <span className="flex min-w-0 items-center gap-1">
                  <Percent size={13} strokeWidth={2} aria-hidden="true" />
                  <span className="truncate">
                    {t((m) => m.billDiscount.lineLabel)}
                    {" · "}
                    {billDiscountReasonLabel(t, invoice.billDiscount.reasonCode)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <bdi dir="ltr" data-testid="invoice-bill-discount-amount">
                    −{money(billDiscountGross)}
                  </bdi>
                  {canDiscount && isOpen && onDiscountRemove ? (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={onDiscountRemove}
                      data-testid="invoice-bill-discount-remove"
                      className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-extrabold text-[color:var(--danger)] underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      {t((m) => m.billDiscount.remove)}
                    </button>
                  ) : null}
                </span>
              </div>
            ) : null}
            {canDiscount && isOpen && onDiscount && liveTotals && liveTotals.subtotal > 0 ? (
              <button
                type="button"
                disabled={isBusy}
                onClick={onDiscount}
                data-testid="invoice-bill-discount-open"
                className="flex min-h-[32px] items-center gap-1 self-start rounded-[var(--radius-control)] border border-dashed border-[color:var(--border-strong)] px-2.5 text-[length:var(--font-size-xs)] font-extrabold text-[color:var(--text-muted)] hover:text-[color:var(--text)] disabled:opacity-50"
              >
                <Percent size={13} strokeWidth={2} aria-hidden="true" />
                {invoice?.billDiscount
                  ? t((m) => m.billDiscount.actionChange)
                  : t((m) => m.billDiscount.action)}
              </button>
            ) : null}
          </div>

          {/* B3 — MONEY IS ALREADY IN THIS BILL.

              Beside the total rather than instead of it: `total` still says
              what the cheque said, and "how much is still owed" is a different
              question. A cashier walking up to a CLOSED bill somebody else took
              300 against has no other way to know before tapping Settle, and
              that is the tap that used to take the full total a second time. */}
          {invoice && invoice.tenderedTotal > 0 && invoice.status !== "SETTLED" ? (
            <div
              className="flex flex-col gap-0.5 tabular-nums"
              data-testid="invoice-partly-paid"
            >
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>{t((m) => m.pos.partialPaidSoFar)}</span>
                <span>{money(invoice.tenderedTotal)}</span>
              </div>
              {/* B89 — WHICH WAY THE MONEY GOES. A bill shortened after payment
                  owes the CUSTOMER, and printing "still owed 0.00" on it (which
                  is what the floored outstanding reads as) says the opposite of
                  the truth. Two lines, never both: one of the two figures is
                  always zero. */}
              {overpaid > 0 ? (
                <div className="flex justify-between text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold text-[color:var(--danger)]">
                  <span>{t((m) => m.pos.overpaidLabel)}</span>
                  <span data-testid="invoice-overpaid">{money(overpaid)}</span>
                </div>
              ) : (
                <div className="flex justify-between text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold text-[color:var(--warning)]">
                  <span>{t((m) => m.pos.partialOutstanding)}</span>
                  <span data-testid="invoice-outstanding">
                    {money(outstandingOnBill(invoice))}
                  </span>
                </div>
              )}
            </div>
          ) : null}

          {/* THE ACT THAT FINISHES SUCH A BILL (B89).

              Said in words and offered in the same breath, because the state it
              describes is one a cashier has no other way out of: nothing is
              owed, so no tender can be taken, and before this the bill simply
              could not be closed. Whoever lacks the refund rung is told WHO to
              ask rather than shown a dead control — the same shape the shift
              warnings take, and for the same reason: no preset role carries
              that grant. */}
          {overpaid > 0 ? (
            <div
              className="flex flex-col gap-1.5 rounded-[var(--radius-list)] bg-[color:var(--danger-100,var(--surface-alt))] px-3 py-2.5"
              data-testid="invoice-overpaid-notice"
            >
              <span className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--danger)]">
                {t((m) => m.pos.overpaidNotice, { amount: money(overpaid) })}
              </span>
              {/* The same gate the overlay copy carries: the dialog only opens
                  on a CLOSED bill, so on a SETTLED one this was a button that
                  set a flag and rendered nothing. */}
              {canRefund && invoice.status === "CLOSED" ? (
                <Button
                  variant="danger"
                  size="touch"
                  fullWidth
                  className="font-extrabold"
                  disabled={isBusy}
                  isLoading={busyKey === "overpaid"}
                  data-testid="invoice-overpaid-refund"
                  onClick={onRefundOverpayment}
                >
                  {t((m) => m.pos.overpaidAction)}
                </Button>
              ) : null}
            </div>
          ) : null}


          {/* The same sentence one rung later. A waiter who closes a bill and
            then finds a dead Settle button has no way to know whether the till
            is broken or the permission is not theirs — the close gate above
            explains itself and this one did not. */}
          {/* D120's "sent to the cashier at …" line left the foot (founder,
              2026-10-06): the floor tile's "waiting for cashier" badge and the
              chime carry it. */}
          {/* No "settling needs a cashier" sentence: without the grant the
              Settle control is not offered at all (founder, 2026-10-06). */}

          {/* D22 still refuses a close over unsent or unstarted plates; the
              sentence saying so left the foot (founder, 2026-10-06), and the
              disabled Close with Send to kitchen above it says it instead. */}

          {/* `flex-wrap` — three buttons do not fit this pane's 371px at the
              standard desktop width. Unwrapped, the row's content ran to 475px
              in English and 410px in Arabic and the third button was PAINTED
              OUTSIDE THE WINDOW (104px past the right edge in English, 39px
              past the left in Arabic), with nothing to scroll it back into
              view and "Close invoice" reading "Cl". Nothing was blocked —
              `invoice-close` is disabled while an unsent line exists, which is
              the only state that draws all three — but a primary action on the
              busiest screen in the product was off-screen in both languages.
              Wrapping puts it on its own line at full width instead. */}
          {isOpen && closeNote ? (
            <p
              className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]"
              data-testid="invoice-close-note"
            >
              {closeNote}
            </p>
          ) : null}
          {/* T6 / D173 — the greyed Close's hidden reason and its one tap
              (`closeBlockedNote`), between the totals and the button row: the
              place the «تقسيم الفاتورة» row held until it left the foot (user,
              2026-10-20). A paper workflow's «طباعة التذكرة» is not here: it is
              the printer's first choice (D182, second ruling), and this
              note keeps only the hidden reason naming the printer. */}
          {closeBlockedNote}
          <div className="flex flex-wrap gap-2">
            {isOpen ? (
              <>
                {/* The walk-away: diners left before anything was fired. Shown
                  only while the server would accept it — one committed plate
                  and the button disappears rather than offering a refusal. */}
                {canCancel ? (
                  <Button
                    variant="danger-outline"
                    size="touch"
                    className="rounded-[var(--radius-list)] font-bold"
                    disabled={isBusy}
                    isLoading={busyKey === "cancel"}
                    data-testid="invoice-cancel"
                    onClick={onCancel}
                  >
                    {t((m) => m.invoices.cancelInvoice)}
                  </Button>
                ) : null}
                {drafts.length > 0 && primary?.permitted !== false ? (
                  <Button
                    variant="primary"
                    size="touch"
                    /* `max-lg:hidden` (waiter phone, 2026-10-28): below `lg` the
                       summary bar carries Send on both panes, so this one
                       would be the same button twice. */
                    className="flex-[1.3] rounded-[var(--radius-list)] font-extrabold max-lg:hidden"
                    /* The primary slot's own answer (UX-round D14): for the life of an
                       OPEN bill this IS the primary, and `enabled` folds in
                       both the grant and "there is something to send". */
                    disabled={!primary?.enabled || isBusy}
                    isLoading={
                      busyKey ===
                      (primary?.kind === "requestCashier" ? "request-cashier" : "fire")
                    }
                    data-testid={
                      primary?.kind === "requestCashier" ? "invoice-request-cashier" : "invoice-send"
                    }
                    onClick={primary?.kind === "requestCashier" ? onRequestCashier : onFire}
                    leadingIcon={
                      paper ? (
                        primary?.kind === "requestCashier" ? (
                          <BellRing size={17} strokeWidth={2} aria-hidden="true" />
                        ) : (
                          <ChefHat size={17} strokeWidth={2} aria-hidden="true" />
                        )
                      ) : undefined
                    }
                  >
                    {primary?.kind === "requestCashier"
                      ? t((m) => m.pos.sendToCashier)
                      : t((m) => m.pos.sendToKitchen)}
                  </Button>
                ) : null}
                {/* ROUND 5 — the pre-bill check, beside the act that ends the
                    bill and never instead of it: the table stays open. An
                    outline control in both looks; PAPER draws it as the
                    design's 42px printer square, with the words as its name. */}
                {showCheck || ticketOnPrinter ? (
                  <span className="relative inline-flex">
                    <Button
                      variant="outline"
                      size="touch"
                      className={paper ? undefined : "rounded-[var(--radius-list)] font-bold"}
                      disabled={isBusy}
                      leadingIcon={
                        <Printer size={paper ? 15 : 16} strokeWidth={1.8} aria-hidden="true" />
                      }
                      /* A name only where there are no words on the control: the
                         PAPER square. The default look's visible label IS its
                         name, and a different aria-label would break 2.5.3. */
                      aria-label={
                        paper
                          ? invoice.lastCheck
                            ? t((m) => m.preBillCheck.actionReprint)
                            : t((m) => m.preBillCheck.action)
                          : undefined
                      }
                      title={
                        ticketOnPrinter && closeReasonText
                          ? closeReasonText
                          : invoice.lastCheck
                            ? t((m) => m.preBillCheck.actionReprint)
                            : t((m) => m.preBillCheck.actionHint)
                      }
                      aria-describedby={ticketOnPrinter ? "invoice-check-attention" : undefined}
                      data-attention={ticketOnPrinter ? "" : undefined}
                      data-testid="invoice-check"
                      /* While a ticket is stuck the printer always asks, so
                         «طباعة التذكرة» is its first choice. */
                      onClick={ticketOnPrinter ? onCheck : (printPicked ?? onCheck)}
                    >
                      {paper ? undefined : t((m) => m.preBillCheck.action)}
                    </Button>
                    {/* The attention dot: an unprinted ticket holds the close. */}
                    {ticketOnPrinter ? (
                      <>
                        <span
                          aria-hidden="true"
                          data-testid="invoice-check-dot"
                          className="pointer-events-none absolute -top-1 -end-1 h-2.5 w-2.5 rounded-full bg-[color:var(--warning)] ring-2 ring-[color:var(--surface)]"
                        />
                        <span id="invoice-check-attention" className="sr-only" data-testid="invoice-check-attention">
                          {closeReasonText}
                        </span>
                      </>
                    ) : null}
                  </span>
                ) : null}
                {paper && onSettleOpen ? (
                  /* PAPER (2026-10-03) — the redesign's Settle, beside the
                     printer square: it opens the Settle window, whose Pay
                     closes the bill and settles it. The same D22 gate as Close
                     (the window closes the bill), and the design's two
                     weights: the fill once nothing is waiting to be sent. */
                  <Button
                    variant={drafts.length > 0 ? "outline" : "primary"}
                    size="touch"
                    className="flex-1 font-extrabold"
                    // D126: Settle no longer waits for the kitchen — the
                    // window takes the money and leaves the bill open.
                    disabled={isBusy}
                    isLoading={busyKey === "close" || busyKey === "settle"}
                    leadingIcon={
                      <ReceiptText size={16} strokeWidth={1.8} aria-hidden="true" />
                    }
                    data-testid="invoice-settle-open"
                    onClick={settlePicked ?? onSettleOpen}
                  >
                    {t((m) => m.pos.settle)}
                  </Button>
                ) : close && dispatchFoot ? (
                  <>
                    {dispatchFoot.kind === "assign" ? (
                      <Button
                        variant={drafts.length > 0 ? "outline" : "primary"}
                        size="touch"
                        /* Its own full-width row at the foot's end, so the
                           cancel and the small close stay together above it. */
                        className="order-last basis-full rounded-[var(--radius-list)] font-extrabold"
                        disabled={dispatchFoot.assignDisabled || isBusy}
                        aria-describedby={dispatchFoot.assignDisabled ? dispatchFoot.describedBy : undefined}
                        title={dispatchFoot.assignDisabled ? (dispatchFoot.assignTitle ?? undefined) : undefined}
                        leadingIcon={<Bike size={16} strokeWidth={2} aria-hidden="true" />}
                        data-testid="invoice-assign-driver"
                        onClick={dispatchFoot.onAssign}
                      >
                        {t((m) => m.dispatch.actionAssign)}
                      </Button>
                    ) : null}
                    {/* The close without an app driver: small, beside it — the
                        existing close / hand-over flow, nothing lost. */}
                    <Button
                      variant="ghost"
                      size="touch"
                      className="shrink-0 rounded-[var(--radius-list)] font-bold"
                      disabled={!close.enabled || isBusy}
                      isLoading={busyKey === "close"}
                      title={closeReasonTitle}
                      aria-describedby={closeReasonTitle ? closeBlockedReasonId("panel") : undefined}
                      data-testid="invoice-close"
                      data-role="secondary"
                      onClick={onClose}
                    >
                      {t((m) => m.pos.close)}
                    </Button>
                    {dispatchFoot.kind === "assign" && dispatchFoot.assignReason ? (
                      <p
                        id={dispatchFoot.describedBy}
                        role="status"
                        data-testid="invoice-assign-reason"
                        className="sr-only"
                      >
                        {dispatchFoot.assignReason}
                      </p>
                    ) : null}
                  </>
                ) : close ? (
                <Button
                  /* NEVER `primary`, whatever the basket is doing (UX-round D15/X2).
                     It used to lift to the primary fill the instant the basket
                     emptied — a bill-ending act taking on the recommended-act
                     costume because data moved. Close is a secondary control
                     on both surfaces now, and Send keeps the fill.
                     2026-10-20 (re-gate): the one exception is the HAND-OVER —
                     a driver accepted and nothing is left to send, so handing
                     the bag over IS the next act: it takes the fill. */
                  variant={handOver && closeAs !== "settle" && drafts.length === 0 ? "primary" : "outline"}
                  size="touch"
                  className="flex-1 rounded-[var(--radius-list)] font-extrabold tabular-nums"
                  // D22, via `tillCloseAction`: a plate the kitchen never
                  // received must not be closed over — that charges a customer
                  // for food nobody made. As Settle (the dark direct takeaway)
                  // it does not wait: the sheet takes the money and leaves the
                  // bill open while the kitchen cooks (D126).
                  disabled={(closeAs !== "settle" && !close?.enabled) || isBusy}
                  isLoading={busyKey === "close" || (closeAs === "settle" && busyKey === "settle")}
                  title={closeAs !== "settle" ? closeReasonTitle : undefined}
                  aria-describedby={closeAs !== "settle" && closeReasonTitle ? closeBlockedReasonId("panel") : undefined}
                  data-testid="invoice-close"
                  onClick={onClose}
                >
                  {closeAs === "settle"
                    ? t((m) => m.pos.settle)
                    : handOver
                      ? t((m) => m.dispatch.handOverToDriver)
                      : t((m) => m.pos.close)}
                </Button>
                ) : null}
              </>
            ) : invoice.status === "CLOSED" ? (
              <>
                {/* The undo for a mistaken close: `INVOICE_REOPEN` (its own
                  rung since 2026-11-02), and gone once settled — money moved
                  is not undoable from a till. */}
                {canReopen ? (
                  <Button
                    variant="outline"
                    size="touch"
                    className="rounded-[var(--radius-list)] font-bold"
                    disabled={isBusy}
                    isLoading={busyKey === "reopen"}
                    data-testid="invoice-reopen"
                    onClick={onReopen}
                  >
                    {t((m) => m.pos.reopen)}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  size="touch"
                  className="flex-1 rounded-[var(--radius-list)] font-extrabold"
                  onClick={onPrint}
                  data-testid="invoice-print"
                >
                  {t((m) => m.pos.print)}
                </Button>
                {canSettle ? (
                <Button
                  variant="primary"
                  size="touch"
                  className="flex-1 rounded-[var(--radius-list)] font-extrabold"
                  disabled={isBusy}
                  isLoading={busyKey === "settle"}
                  data-testid="invoice-settle"
                  onClick={settlePicked ?? onSettle}
                >
                  {t((m) => m.pos.settle)}
                </Button>
                ) : null}
              </>
            ) : (
              <Button
                variant="outline"
                size="touch"
                className="flex-1 rounded-[var(--radius-list)] font-extrabold"
                onClick={onPrint}
                data-testid="invoice-print"
              >
                {t((m) => m.pos.print)}
              </Button>
            )}
          </div>

          {/* The check's freshness left the foot (founder, 2026-10-07); the
              floor tile still says "check printed" / "check out of date". */}
        </div>
      ) : null}
      {paper && actionLine ? (
        <LineActionsSheet
          group={actionLine.group}
          name={localName({
            nameAr: actionLine.group.plateNameAr,
            nameEn: actionLine.group.plateNameEn,
          })}
          total={money(actionLine.group.total)}
          isBusy={isBusy}
          onClose={() => setActionLine(null)}
          actions={(() => {
            const group = actionLine.group;
            if (actionLine.kind === "draft") {
              // Not sent yet: the guest may still change it.
              const draft: LineAction[] = [];
              if (onDraftEdit) {
                draft.push({ key: "edit", icon: Pencil, label: t((m) => m.pos.lineActionEdit), run: () => onDraftEdit(group) });
              }
              if (canSubstitute) {
                draft.push({ key: "swap", icon: ArrowLeftRight, label: t((m) => m.pos.swapAmend), run: () => onAmendSwaps(group) });
              }
              if (canAppend) {
                draft.push({ key: "note", icon: StickyNote, label: t((m) => m.pos.lineActionNote), run: () => onEditNote(group) });
              }
              draft.push({ key: "remove", icon: Trash2, label: t((m) => m.pos.lineRemove), tone: "danger", run: () => onDraftRemove(group) });
              return draft;
            }
            const state = group.fulfillmentState;
            const comped = group.billingState === "COMPED";
            const queued = state === "QUEUED";
            const list: LineAction[] = [];
            // No Edit once sent (owner, 2026-10-11): changing a sent plate
            // means taking it off, which is a void with a reason (Cancel
            // below, the cashier's), then adding the new one.
            if (canPrepare && state === "PREPARING") {
              list.push({ key: "ready", icon: CircleCheck, label: t((m) => m.kitchen.actionReady), run: () => onMarkReady(group) });
            }
            if (canServe && (state === "PREPARING" || state === "READY")) {
              list.push({ key: "serve", icon: HandPlatter, label: t((m) => m.pos.serveAction), tone: "success", run: () => onServe(group) });
            }
            if (
              canSubstitute &&
              (queued || (kitchenPrintsChits && state === "PREPARING")) &&
              !comped
            ) {
              list.push({ key: "swap", icon: ArrowLeftRight, label: t((m) => m.pos.swapAmend), run: () => onAmendSwaps(group) });
            }
            if (canRemake) {
              list.push({
                key: "remake",
                icon: RotateCcw,
                label: t((m) => m.pos.remake),
                muted: queued,
                run: () => (queued ? onRemakeBlocked(group) : onRemake(group)),
              });
            }
            if (canComp && !comped) {
              list.push({ key: "comp", icon: Gift, label: t((m) => m.pos.comp), run: () => onSentVoid(group, true) });
            }
            if (canPrint && group.plates[0].kitchenTicketId) {
              const ticketId = group.plates[0].kitchenTicketId;
              list.push({ key: "print", icon: Printer, label: t((m) => m.pos.printChit), run: () => onPrintChit(ticketId) });
            }
            if (canVoid) {
              list.push({ key: "cancel", icon: Trash2, label: t((m) => m.pos.lineCancel), tone: "danger", run: () => onSentVoid(group, false) });
            }
            return list;
          })()}
        />
      ) : null}
    </aside>
  );
}

type LineAction = {
  key: string;
  /** Large, so the act reads before its label does (founder, 2026-10-06). */
  icon: LucideIcon;
  label: string;
  run: () => void;
  tone?: "danger" | "success";
  /** Offered but explains itself on tap (a remake before the kitchen starts). */
  muted?: boolean;
};

/**
 * A sent line's acts, one tap away (founder, 2026-10-06): the bill row keeps
 * a single action (Start) and this sheet carries the rest, each offered only
 * where the line's state and the session's permissions allow it — the same
 * rules the row's chips used. Choosing one runs it and closes the sheet.
 */
function LineActionsSheet({
  group,
  name,
  total,
  actions,
  isBusy,
  onClose,
}: {
  group: PlateGroup;
  name: string;
  total: string;
  actions: LineAction[];
  isBusy: boolean;
  onClose: () => void;
}) {
  const t = useTranslate();
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={name}
      subtitle={`×${group.quantity} · ${total}`}
      closeLabel={t((m) => m.common.close)}
      hideFooter
      data-testid="line-actions"
    >
      <div className="grid grid-cols-3 gap-2.5" data-ui="line-actions">
        {actions.length === 0 ? (
          <p className="col-span-3 py-2 text-sm text-[color:var(--text-muted)]">
            {t((m) => m.pos.lineActionsNone)}
          </p>
        ) : (
          actions.map((action) => (
            <button
              key={action.key}
              type="button"
              disabled={isBusy}
              data-ui="line-action"
              data-tone={action.tone}
              data-testid={`line-action-${action.key}`}
              onClick={() => {
                action.run();
                onClose();
              }}
              className={[
                "flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-[var(--radius-tile,12px)] border px-2 py-3 text-center text-[length:var(--font-size-sm)] font-semibold leading-tight transition-colors disabled:opacity-40",
                action.tone === "danger"
                  ? "border-[color:var(--danger)] text-[color:var(--danger)] enabled:hover:bg-[color:var(--error-50,var(--surface-alt))]"
                  : action.tone === "success"
                    ? "border-[color:var(--success)] text-[color:var(--success)] enabled:hover:bg-[color:var(--success-100,var(--surface-alt))]"
                    : "border-[color:var(--border-strong)] text-[color:var(--text)] enabled:hover:bg-[color:var(--surface-alt)]",
                action.muted ? "opacity-50" : "",
              ].join(" ")}
            >
              <action.icon size={28} strokeWidth={1.75} aria-hidden="true" />
              <span>{action.label}</span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
